import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { X, Download, Pin, FileText, AlertCircle, Lightbulb, Copy, Check, ExternalLink, ZoomIn, ZoomOut, RotateCcw, RotateCw, Image as ImageIcon, MessageSquare, MessageSquarePlus, History, Loader2, ChevronUp, ChevronDown, ChevronLeft, Trash2, Sparkles, Minimize2, Maximize2, Type, Edit3, Search, ChevronsUpDown, ChevronsDown, ChevronsUp } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { documentApi, DocumentContent, chatApi, taskApi, userApi, DocumentComment } from '@/services/api';
import { API_CONFIG } from '@/config/api.config';
import Markdown from 'markdown-to-jsx';
import { CustomQuestionSection } from './CustomQuestionSection';
import { CustomPromptModal } from './CustomPromptModal';
import { useSessionStore, useNotificationStore } from '@/stores';
import { useTaskResultCopy } from '@/hooks/useTaskResultCopy';
import { useCompactMode } from '@/hooks/useCompactMode';
import { useDocumentFontSize, DOC_FONT_SIZE_OPTIONS } from '@/hooks/useDocumentFontSize';
import { standardDocumentPrompts } from '@/config/standardDocumentPrompts';
import { TaskResultAttachment } from '@/stores/sessionStore';
import PartsListDisplay from './task-displays/PartsListDisplay';
import SpecMapDisplay from './task-displays/SpecMapDisplay';
import ClaimsSupportDisplay from './task-displays/ClaimsSupportDisplay';
import ClaimChartDisplay from './task-displays/ClaimChartDisplay';
import ClaimsWithNumeralsDisplay from './task-displays/ClaimsWithNumeralsDisplay';
import CustomAnalysisDisplay from './task-displays/CustomAnalysisDisplay';
import IRMetadataDisplay from './task-displays/IRMetadataDisplay';
import IRInnovationAnalysisDisplay from './task-displays/IRInnovationAnalysisDisplay';
import IRTechClassificationDisplay from './task-displays/IRTechClassificationDisplay';
import DraftSummaryDisplay from './task-displays/DraftSummaryDisplay';
import DraftDetailedDescriptionDisplay from './task-displays/DraftDetailedDescriptionDisplay';
import DraftBackgroundDisplay from './task-displays/DraftBackgroundDisplay';
import DraftClaimsDisplay from './task-displays/DraftClaimsDisplay';
import OtherEmbodimentsDisplay from './task-displays/OtherEmbodimentsDisplay';
import OAExtractDisplay from './task-displays/OAExtractDisplay';
import OAAnalyzeDisplay from './task-displays/OAAnalyzeDisplay';
import OASummaryArtDisplay from './task-displays/OASummaryArtDisplay';
import OATimelineDisplay from './task-displays/OATimelineDisplay';
import TranslateDocumentDisplay from './task-displays/TranslateDocumentDisplay';
import { AVAILABLE_TASKS } from '@/types/taskTypes';

interface DocumentViewerProps {
  document: {
    resource_id?: string;
    file_name?: string;
    file_type?: string;
    type?: string; // from active documents
    title?: string; // from active documents
  } | null;
  onClose: () => void;
  isFullWidth?: boolean;
  onViewApplication?: (applicationNumber: string) => void;
  initialTab?: string; // Tab to switch to when opening (e.g., 'specification', 'claims')
  scrollToId?: string; // Element ID to scroll to after opening (e.g., 'p-0004', 'claim-1')
  hideCloseButton?: boolean; // Hide the X button (e.g., when in fullscreen modal that has its own close)
  hideFullscreenButton?: boolean; // Hide the fullscreen button (e.g., when already in fullscreen)
  initialTaskResult?: TaskResultAttachment | null; // Auto-open task history with this result pre-selected
}

export function DocumentViewer({ document, onClose, isFullWidth = false, onViewApplication, initialTab, scrollToId, hideCloseButton = false, hideFullscreenButton = false, initialTaskResult = null }: DocumentViewerProps) {
  const scrollTimestamp = (document as any)?._scrollTimestamp;
  const sessionApplications = useSessionStore(state => state.sessionApplications);
  const { addNotification } = useNotificationStore();
  const { showToast, copyResult } = useTaskResultCopy();

  const [loading, setLoading] = useState(true);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [documentContent, setDocumentContent] = useState<DocumentContent | null>(null);
  const [copiedParagraph, setCopiedParagraph] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Highlighting state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [claimsFilterText, setClaimsFilterText] = useState('');
  const [debouncedClaimsFilterText, setDebouncedClaimsFilterText] = useState('');
  const [claimsFilterEnabled, setClaimsFilterEnabled] = useState(false);
  const [searchMatchCount, setSearchMatchCount] = useState(0);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [filterSearchResults, setFilterSearchResults] = useState(false);
  const [claimsFilterMatchCount, setClaimsFilterMatchCount] = useState(0);
  const [currentClaimsFilterIndex, setCurrentClaimsFilterIndex] = useState(0);
  const [referenceNumeral, setReferenceNumeral] = useState('');
  const [debouncedReferenceNumeral, setDebouncedReferenceNumeral] = useState('');
  const [referenceNumeralMatchCount, setReferenceNumeralMatchCount] = useState(0);
  const [currentReferenceNumeralIndex, setCurrentReferenceNumeralIndex] = useState(0);
  const [filterReferenceNumerals, setFilterReferenceNumerals] = useState(false);

  // Pinned highlights state - array of {term, type: 'search' | 'reference', color, cssColor}
  // Using both Tailwind classes (for UI badges) and CSS hex values (for inline styles in content)
  const PINNED_COLORS = [
    { class: 'bg-pink-300', css: '#f9a8d4' },
    { class: 'bg-purple-300', css: '#d8b4fe' },
    { class: 'bg-blue-300', css: '#93c5fd' },
    { class: 'bg-cyan-300', css: '#67e8f9' },
    { class: 'bg-orange-300', css: '#fdba74' },
    { class: 'bg-rose-300', css: '#fda4af' },
    { class: 'bg-indigo-300', css: '#a5b4fc' },
    { class: 'bg-teal-300', css: '#5eead4' },
  ];
  const [pinnedHighlights, setPinnedHighlights] = useState<Array<{term: string, type: 'search' | 'reference', color: string, cssColor: string}>>([]);

  // Comments state
  const [comments, setComments] = useState<DocumentComment[]>([]);
  const [showCommentsPanel, setShowCommentsPanel] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionParagraphIndex, setSelectionParagraphIndex] = useState<number | null>(null);
  const [selectionSection, setSelectionSection] = useState('');
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const [commentPopupPosition, setCommentPopupPosition] = useState({ x: 0, y: 0 });
  const [newCommentText, setNewCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const taskHistoryContainerRef = useRef<HTMLDivElement>(null);
  const taskResultViewerRef = useRef<HTMLDivElement>(null);

  // Collapsible search state
  const [showSearchControls, setShowSearchControls] = useState(false);

  const pinCurrentHighlight = (type: 'search' | 'reference') => {
    const term = type === 'search' ? debouncedSearchQuery : debouncedReferenceNumeral;
    if (!term.trim()) return;

    // Check if already pinned
    if (pinnedHighlights.some(p => p.term.toLowerCase() === term.toLowerCase() && p.type === type)) return;

    // Get next available color
    const usedColors = pinnedHighlights.map(p => p.color);
    const nextColor = PINNED_COLORS.find(c => !usedColors.includes(c.class)) || PINNED_COLORS[pinnedHighlights.length % PINNED_COLORS.length];

    setPinnedHighlights([...pinnedHighlights, { term, type, color: nextColor.class, cssColor: nextColor.css }]);
  };

  const unpinHighlight = (term: string, type: 'search' | 'reference') => {
    setPinnedHighlights(pinnedHighlights.filter(p => !(p.term.toLowerCase() === term.toLowerCase() && p.type === type)));
  };

  const clearAllPinnedHighlights = () => {
    setPinnedHighlights([]);
  };

  // Helper function to apply unified highlighting (search, reference numerals, pinned)
  const applyUnifiedHighlighting = (content: string, options?: { includeRefNum?: boolean }) => {
    const query = debouncedSearchQuery.trim();
    const refNum = options?.includeRefNum ? debouncedReferenceNumeral.trim() : '';
    const hasPinnedHighlights = pinnedHighlights.length > 0;

    if (!query && !refNum && !hasPinnedHighlights) {
      return content;
    }

    const matches: Array<{ start: number; end: number; type: string; text: string; cssColor?: string }> = [];
    const lowerContent = content.toLowerCase();

    // Find all query matches
    if (query) {
      const lowerQuery = query.toLowerCase();
      let pos = 0;
      while ((pos = lowerContent.indexOf(lowerQuery, pos)) !== -1) {
        matches.push({
          start: pos,
          end: pos + query.length,
          type: 'search',
          text: content.substring(pos, pos + query.length)
        });
        pos++;
      }
    }

    // Find all reference numeral matches
    if (refNum) {
      const lowerRefNum = refNum.toLowerCase();
      let pos = 0;
      while ((pos = lowerContent.indexOf(lowerRefNum, pos)) !== -1) {
        matches.push({
          start: pos,
          end: pos + refNum.length,
          type: 'refnum',
          text: content.substring(pos, pos + refNum.length)
        });
        pos++;
      }
    }

    // Find all pinned highlight matches
    if (hasPinnedHighlights) {
      pinnedHighlights.forEach(pinned => {
        const lowerTerm = pinned.term.toLowerCase();
        let pos = 0;
        while ((pos = lowerContent.indexOf(lowerTerm, pos)) !== -1) {
          matches.push({
            start: pos,
            end: pos + pinned.term.length,
            type: 'pinned',
            text: content.substring(pos, pos + pinned.term.length),
            cssColor: pinned.cssColor
          });
          pos++;
        }
      });
    }

    if (matches.length === 0) {
      return content;
    }

    // Sort by length (longer first) to ensure longer matches take priority
    matches.sort((a, b) => {
      const lenDiff = (b.end - b.start) - (a.end - a.start);
      if (lenDiff !== 0) return lenDiff;
      return a.start - b.start;
    });

    // Build non-overlapping ranges
    const usedRanges: typeof matches = [];
    matches.forEach(match => {
      const overlaps = usedRanges.some(range =>
        (match.start >= range.start && match.start < range.end) ||
        (match.end > range.start && match.end <= range.end) ||
        (match.start <= range.start && match.end >= range.end)
      );
      if (!overlaps) {
        usedRanges.push(match);
      }
    });

    // Sort by position for building result
    usedRanges.sort((a, b) => a.start - b.start);

    // Build highlighted text
    let result = '';
    let lastEnd = 0;

    usedRanges.forEach(match => {
      if (match.start > lastEnd) {
        result += content.substring(lastEnd, match.start);
      }

      if (match.type === 'search') {
        result += `<mark style="background-color: #fde047; color: black;">${match.text}</mark>`;
      } else if (match.type === 'refnum') {
        result += `<mark style="background-color: #93c5fd; color: black;">${match.text}</mark>`;
      } else if (match.type === 'pinned') {
        result += `<mark style="background-color: ${match.cssColor}; color: black;">${match.text}</mark>`;
      }

      lastEnd = match.end;
    });

    if (lastEnd < content.length) {
      result += content.substring(lastEnd);
    }

    return result;
  };

  // Check if any highlighting is active
  const hasAnyHighlighting = debouncedSearchQuery.trim() || debouncedReferenceNumeral.trim() || pinnedHighlights.length > 0;

  // Pinning state
  const [claimsPinned, setClaimsPinned] = useState(false);
  const [drawingsPinned, setDrawingsPinned] = useState(false);

  // Drawings state
  const [selectedDrawingIndex, setSelectedDrawingIndex] = useState<number | null>(null);
  const [drawingZoom, setDrawingZoom] = useState(100);
  const [drawingRotation, setDrawingRotation] = useState(0);
  const [drawingViewerWidth, setDrawingViewerWidth] = useState(320);
  const [isResizingDrawingWidth, setIsResizingDrawingWidth] = useState(false);
  const [drawingsUrls, setDrawingsUrls] = useState<string[]>([]);
  const [loadingDrawings, setLoadingDrawings] = useState(false);

  // EPO drawings state (for foreign patents)
  type EpoDrawingMeta = { link: string; description: string; num_pages: number };
  const [epoDrawings, setEpoDrawings] = useState<EpoDrawingMeta[]>([]);
  const [epoSelectedDrawing, setEpoSelectedDrawing] = useState<number | null>(null);
  const [epoCurrentPage, setEpoCurrentPage] = useState(1);
  const [epoDrawingBlobUrl, setEpoDrawingBlobUrl] = useState<string | null>(null);
  const [epoLoadingPage, setEpoLoadingPage] = useState(false);

  // Question History state
  const [showQuestionHistory, setShowQuestionHistory] = useState(false);
  const [showQuestionHistoryFullscreen, setShowQuestionHistoryFullscreen] = useState(false);
  const [questions, setQuestions] = useState<Array<{
    input_text: string;
    response: string | { output: string };
    timestamp: string;
  }>>([]);
  const [questionText, setQuestionText] = useState('');
  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionWidth, setQuestionWidth] = useState(370);
  const [isResizingQuestion, setIsResizingQuestion] = useState(false);
  const [showTopButton, setShowTopButton] = useState(false);
  const [showBottomButton, setShowBottomButton] = useState(false);
  const [customPrompts, setCustomPrompts] = useState<Array<{ prompt_id: string; label: string; prompt: string; available_in?: string }>>([]);
  const [showCustomPromptModal, setShowCustomPromptModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<{ id: string; label: string; prompt: string; available_in?: string } | null>(null);
  const [showQuickPromptsMenu, setShowQuickPromptsMenu] = useState(false);
  const { compactMode, setCompactMode } = useCompactMode();
  const { documentFontSize, setDocumentFontSize, getFontSizeStyle } = useDocumentFontSize();

  // Filter custom prompts for document context (document or both)
  const filteredCustomPrompts = useMemo(() => {
    return customPrompts.filter(p =>
      !p.available_in || p.available_in === 'document' || p.available_in === 'both'
    );
  }, [customPrompts]);

  // Task History state
  const [showTaskHistory, setShowTaskHistory] = useState(false);
  const [taskHistory, setTaskHistory] = useState<Array<{
    analysis_id: string;
    task_type: string;
    task_name?: string;
    result?: any;
    analysis?: any;
    timestamp?: string;
    time_received?: string;
    doc_id?: string;
    selected_number?: string;
    application_number?: string;
  }>>([]);
  const [taskHistoryLoading, setTaskHistoryLoading] = useState(false);
  const [taskHistoryWidth, setTaskHistoryWidth] = useState(370);
  const [isResizingTaskHistory, setIsResizingTaskHistory] = useState(false);
  const [selectedTaskResult, setSelectedTaskResult] = useState<TaskResultAttachment | null>(null);
  const [showTaskResultViewer, setShowTaskResultViewer] = useState(false);
  const [showTaskResultFullscreen, setShowTaskResultFullscreen] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);

  // Auto-open task history panel with a pre-selected result when initialTaskResult is provided
  useEffect(() => {
    if (initialTaskResult) {
      setShowTaskHistory(true);
      setSelectedTaskResult(initialTaskResult);
    }
  }, [initialTaskResult]);

  // Refs
  const claimsSidebarRef = useRef<HTMLDivElement>(null);
  const drawingViewerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const questionScrollRef = useRef<HTMLDivElement>(null);
  const taskHistoryScrollRef = useRef<HTMLDivElement>(null);
  const lastLoadedDocKey = useRef<string | null>(null);

  const loadDocument = useCallback(async () => {
    if (!document) return;

    setLoading(true);
    try {
      // Check if document already has content (e.g., MPEP, CFR, USC)
      if ((document as any).content) {
        setDocumentContent({
          title: (document as any).title || document.file_name || 'Document',
          abstract: '',
          description: '',
          claims: '',
          summary: null,
          content: (document as any).content,
          contentType: (document as any).contentType || 'html'
        } as any);
        setLoading(false);
        return;
      }

      // Determine document type
      let docType = document.file_type || document.type?.toLowerCase() || 'patent';

      if (docType === 'foreign patent') {
        docType = 'foreign_patent';
      }

      let docId = document.resource_id || document.title || '';

      docId = docId.replace(/^(Patent|Publication|Application|Foreign Patent|Upload)\s+/i, '').trim();
      docId = docId.replace(/^(patent-|publication-|application-|foreign_patent-)+/gi, '');

      const numberMatch = docId.match(/\d+/);
      if (numberMatch && docType !== 'upload' && docType !== 'foreign_patent') {
        docId = numberMatch[0];
      }

      console.log('Loading document:', { docType, docId, original: document.resource_id || document.title });

      const content = await documentApi.fetchDocument(docType, docId);

      // "content_unavailable" means the document is known but its stored text could not
      // be read — the backend has queued a reload. Treated exactly like "not_found":
      // both mean there is nothing to show yet and something is fetching it, and the
      // difference matters to the server rather than to the reader. Without this the
      // empty payload was set as the document and the viewer rendered blank.
      const contentStatus = (content as any)?.status;
      const usable =
        content && contentStatus !== 'not_found' && contentStatus !== 'content_unavailable';

      if (usable) {
        setDocumentContent(content);

        if ((docType === 'patent' || docType === 'publication') && sessionApplications) {
          try {
            const cleanDocId = docId.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');

            const matchingApp = sessionApplications.find((app: any) => {
              const metaData = app.application?.applicationMetaData;
              if (!metaData) return false;

              if (docType === 'patent' && metaData.patentNumber) {
                const patentNum = metaData.patentNumber.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                return patentNum === cleanDocId;
              } else if (docType === 'publication' && metaData.earliestPublicationNumber) {
                const pubNum = metaData.earliestPublicationNumber.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                return pubNum === cleanDocId;
              }
              return false;
            });

            if (matchingApp) {
              const appNumber = matchingApp.application_id || matchingApp.application?.applicationNumberText;
              if (appNumber) {
                setDocumentContent(prev => {
                  // Only update if application_number actually changed
                  if (prev && prev.application_number !== appNumber) {
                    return { ...prev, application_number: appNumber };
                  }
                  return prev;
                });
              }
            }
          } catch (error) {
            console.error('Failed to find application number:', error);
          }
        }
      } else {
        if (docType === 'patent' || docType === 'publication') {
          setIsOnboarding(true);

          const reloading = contentStatus === 'content_unavailable';
          const resourceTypeName = docType.charAt(0).toUpperCase() + docType.slice(1);
          addNotification({
            type: 'info',
            title: reloading ? `Reloading ${resourceTypeName}` : `Onboarding ${resourceTypeName}`,
            message: reloading
              ? `The stored text of ${docId} could not be read. Fetching it again...`
              : `${docId} is not in your session. Loading it now...`,
            duration: 10000,
          });

          try {
            const onboardResponse = await documentApi.fetchAndLoadDocument(docType, docId);

            if (onboardResponse?.job_id) {
              const pollForCompletion = async () => {
                const maxAttempts = 60;
                let attempts = 0;

                while (attempts < maxAttempts) {
                  await new Promise(resolve => setTimeout(resolve, 2000));
                  attempts++;

                  try {
                    const retryContent = await documentApi.fetchDocument(docType, docId);
                    const retryStatus = (retryContent as any)?.status;
                    if (
                      retryContent &&
                      retryStatus !== 'not_found' &&
                      retryStatus !== 'content_unavailable'
                    ) {
                      setDocumentContent(retryContent);
                      setIsOnboarding(false);
                      addNotification({
                        type: 'success',
                        title: `${resourceTypeName} Ready`,
                        message: `${docId} has been loaded successfully.`,
                        duration: 5000,
                      });
                      return;
                    }
                  } catch (pollError) {
                    console.log('Polling attempt', attempts, '- document not ready yet');
                  }
                }

                setIsOnboarding(false);
                addNotification({
                  type: 'warning',
                  title: 'Still Loading',
                  message: `${docId} is taking longer than expected. It may appear in your documents shortly.`,
                  duration: 8000,
                });
              };

              pollForCompletion();
            } else {
              setIsOnboarding(false);
              addNotification({
                type: 'error',
                title: 'Failed to Load',
                message: `Could not start loading ${docId}. Please try again.`,
                duration: 5000,
              });
            }
          } catch (onboardError) {
            console.error('Failed to onboard document:', onboardError);
            setIsOnboarding(false);
            addNotification({
              type: 'error',
              title: 'Onboarding Failed',
              message: `Failed to load ${docId}. Please try again.`,
              duration: 5000,
            });
          }
        } else {
          console.error('Failed to load document content');
        }
      }
    } catch (error) {
      console.error('Error loading document:', error);
    } finally {
      setLoading(false);
    }
  }, [addNotification, document, sessionApplications]);

  // Strip leading paragraph number tags like [0001] from paragraph content
  const stripParaNum = (text: string) => text?.replace(/^\[\d{4}\]\s*/, '') ?? text;

  // Get sections from document content
  const getSections = useCallback(() => {
    const description = documentContent?.description;
    console.log('[getSections] description type:', typeof description, 'value:', description);

    if (!description) {
      console.log('[getSections] No description found');
      return [];
    }

    if (typeof description === 'string') {
      console.log('[getSections] Description is a string, returning empty (will use processedDescription)');
      return [];
    }

    if (typeof description === 'object' && !Array.isArray(description)) {
      // Type guard to check if it has sections property
      const hasSection = 'sections' in description;
      console.log('[getSections] Description is object, has sections?:', hasSection);

      if (hasSection) {
        const sectionsData = Array.isArray((description as { sections?: any[] }).sections)
          ? (description as { sections?: any[] }).sections
          : null;

        if (sectionsData) {
          console.log('[getSections] Found sections array with', sectionsData.length, 'sections');
          const sections = sectionsData.map(section => {
            // Find the first paragraph that has a number property
            const firstNumberedPara = section.paragraphs?.find((para: any) => para.number !== undefined);
            const minParaNum = firstNumberedPara ? parseInt(String(firstNumberedPara.number)) : Infinity;

            console.log(`Section "${section.title}": minParagraphNumber = ${minParaNum}`);

            return {
              title: section.title,
              paragraphs: section.paragraphs?.map((para: any) => ({
                number: para.number ? String(para.number).padStart(4, '0') : '',
                displayNumber: para.number ? String(para.number).padStart(4, '0') : '',
                content: stripParaNum(para.text || para.content),
                originalNumber: para.number ? parseInt(String(para.number)) : 0,
                heading: para.heading
              })) || [],
              minParagraphNumber: minParaNum
            };
          });

          return sections.sort((a, b) => a.minParagraphNumber - b.minParagraphNumber);
        }
      }

      // Fallback: Try to parse as flat dictionary (paragraph_number: text)
      const entries = Object.entries(description as Record<string, any>);
      console.log('[getSections] Trying flat dictionary format with', entries.length, 'entries');

      if (entries.length > 0) {
        // Check if keys look like paragraph numbers (either pure numeric or "para_NNNN" format)
        const firstKey = entries[0][0];
        const isNumericKey = /^\d+$/.test(firstKey);
        const isParaKey = /^para_\d+$/.test(firstKey);
        console.log('[getSections] First key:', firstKey, 'is numeric?:', isNumericKey, 'is para_NNNN?:', isParaKey);

        if (isNumericKey) {
          return [{
            title: 'Description',
            paragraphs: entries
              .map(([number, content]) => ({
                number: String(number).padStart(4, '0'),
                displayNumber: String(number).padStart(4, '0'),
                content: stripParaNum(typeof content === 'string' ? content : content?.text || content?.content || String(content))
              }))
              .sort((a, b) => parseInt(a.number) - parseInt(b.number)),
            minParagraphNumber: parseInt(entries[0][0]) || 0
          }];
        } else if (isParaKey) {
          // Handle "para_NNNN" format
          console.log('[getSections] Detected para_NNNN format, extracting paragraph numbers');
          return [{
            title: 'Description',
            paragraphs: entries
              .map(([key, content]) => {
                // Extract number from "para_0012" -> "0012" -> 12
                const match = key.match(/^para_(\d+)$/);
                const paraNum = match ? parseInt(match[1]) : 0;
                return {
                  number: String(paraNum).padStart(4, '0'),
                  displayNumber: String(paraNum).padStart(4, '0'),
                  content: stripParaNum(typeof content === 'string' ? content : content?.text || content?.content || String(content)),
                  originalNumber: paraNum
                };
              })
              .sort((a, b) => a.originalNumber - b.originalNumber),
            minParagraphNumber: 0
          }];
        }
      }
    }

    console.log('[getSections] No valid format found, returning empty');
    return [];
  }, [documentContent?.description]);

  // Handle text selection for adding comments
  const handleTextSelection = useCallback((e: MouseEvent) => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      setShowCommentPopup(false);
      return;
    }

    const text = selection.toString().trim();
    if (text.length < 3) {
      setShowCommentPopup(false);
      return;
    }

    // Find the paragraph index and offset from the selection
    const anchorNode = selection.anchorNode;
    let paragraphIndex = -1;
    let section = 'content';
    let textOffset = 0;

    if (anchorNode) {
      let parent = anchorNode.parentElement;
      const selectionText = selection.toString();

      console.log('[Selection] Selected text:', selectionText.substring(0, 50));
      console.log('[Selection] Current active tab:', activeTab);

      // Determine which tab we're in by checking parent elements
      let tabSection = activeTab; // Default to active tab name

      // Map tab names to section names
      const tabToSection: { [key: string]: string } = {
        'overview': 'overview',
        'specification': 'specification',
        'claims': 'claims',
        'abstract': 'abstract',
        'content': 'content'
      };

      section = tabToSection[activeTab] || activeTab;

      // Get sections data to match against (for specification tab)
      const sections = getSections();
      console.log('[Selection] getSections() returned', sections.length, 'sections');

      if (sections.length > 0) {
        console.log('[Selection] Section titles:', sections.map(s => s.title));
        console.log('[Selection] Total paragraphs:', sections.reduce((sum, s) => sum + s.paragraphs.length, 0));
      }

      // Try to find which section and which paragraph contains the selected text
      let matchedSection: any = null;
      let matchedParagraph: any = null;
      let paragraphTextOffset = -1;

      // If we have sections (specification tab), find the actual selected instance using context
      if (sections.length > 0 && activeTab === 'specification') {
        console.log('[Selection] Finding actual selection using DOM range...');

        // Get the actual DOM range to find exact position
        const range = selection.getRangeAt(0);
        const container = range.startContainer;

        // Find the prose container
        let proseElement = container.nodeType === Node.TEXT_NODE ? container.parentElement : container as HTMLElement;
        while (proseElement && !proseElement.classList.contains('prose') && proseElement !== window.document.body) {
          proseElement = proseElement.parentElement;
        }

        if (proseElement) {
          // Calculate the actual character offset from the start of proseElement to the selection start
          // We need to walk through all text nodes and count characters
          const walker = window.document.createTreeWalker(
            proseElement,
            NodeFilter.SHOW_TEXT,
            null
          );

          let currentOffset = 0;
          let selectionAbsoluteOffset = -1;
          let foundStart = false;

          let node;
          while ((node = walker.nextNode())) {
            if (node === range.startContainer) {
              // Found the node containing the selection start
              selectionAbsoluteOffset = currentOffset + range.startOffset;
              foundStart = true;
              break;
            }
            // Add this node's text length to the offset
            currentOffset += (node.textContent || '').length;
          }

          if (!foundStart) {
            console.log('[Selection] Could not find selection start in text nodes');
            selectionAbsoluteOffset = 0;
          }

          const fullContextText = proseElement.textContent || '';
          console.log('[Selection] Full context length:', fullContextText.length);
          console.log('[Selection] Selection starts at absolute offset:', selectionAbsoluteOffset);

          // Get a unique context snippet (100 chars before + selection + 50 chars after)
          const contextStart = Math.max(0, selectionAbsoluteOffset - 100);
          const contextEnd = Math.min(fullContextText.length, selectionAbsoluteOffset + selectionText.length + 50);
          const contextSnippet = fullContextText.substring(contextStart, contextEnd);
          const selectionOffsetInSnippet = selectionAbsoluteOffset - contextStart;

          console.log('[Selection] Context snippet:', contextSnippet.substring(0, 100) + '...');
          console.log('[Selection] Selection offset within snippet:', selectionOffsetInSnippet);

          // Now search for this context in the paragraphs
          for (const sec of sections) {
            for (const para of sec.paragraphs) {
              if (!para.content) continue;

              // Check if this paragraph contains the context snippet
              const paraText = para.content;
              if (paraText.includes(contextSnippet)) {
                // Found the paragraph! Calculate the exact offset using the selection's position in the snippet
                const snippetOffsetInPara = paraText.indexOf(contextSnippet);
                paragraphTextOffset = snippetOffsetInPara + selectionOffsetInSnippet;

                matchedSection = sec;
                matchedParagraph = para;

                console.log(`[Selection] ✓ Found using context in section "${sec.title}"`);
                console.log(`[Selection] ✓ Paragraph originalNumber: ${para.originalNumber}`);
                console.log(`[Selection] ✓ Offset within paragraph: ${paragraphTextOffset}`);
                break;
              }

              // Also try with normalized whitespace (remove markdown artifacts, etc.)
              const normalizedPara = paraText.replace(/\s+/g, ' ').trim();
              const normalizedSnippet = contextSnippet.replace(/\s+/g, ' ').trim();

              if (normalizedPara.includes(normalizedSnippet)) {
                const snippetOffsetInPara = normalizedPara.indexOf(normalizedSnippet);

                // Map the selection offset from original to normalized space
                let normalizedSelectionOffset = 0;
                let originalIdx = 0;
                for (let i = 0; i < selectionOffsetInSnippet && originalIdx < contextSnippet.length; originalIdx++) {
                  const char = contextSnippet[originalIdx];
                  if (!/\s/.test(char)) {
                    normalizedSelectionOffset++;
                    i++;
                  } else if (normalizedSelectionOffset < normalizedSnippet.length && normalizedSnippet[normalizedSelectionOffset] === ' ') {
                    normalizedSelectionOffset++;
                    i++;
                  }
                }

                paragraphTextOffset = snippetOffsetInPara + normalizedSelectionOffset;

                matchedSection = sec;
                matchedParagraph = para;

                console.log(`[Selection] ✓ Found using normalized context in section "${sec.title}"`);
                console.log(`[Selection] ✓ Paragraph originalNumber: ${para.originalNumber}`);
                console.log(`[Selection] ✓ Offset within paragraph: ${paragraphTextOffset}`);
                break;
              }
            }
            if (matchedParagraph) break;
          }
        }

        if (!matchedParagraph) {
          console.log('[Selection] ⚠ Could not find using context, falling back to simple search...');

          // Fallback: use first occurrence
          for (const sec of sections) {
            for (const para of sec.paragraphs) {
              if (!para.content) continue;
              const offset = para.content.indexOf(selectionText);
              if (offset !== -1) {
                matchedSection = sec;
                matchedParagraph = para;
                paragraphTextOffset = offset;
                console.log(`[Selection] Using first occurrence in paragraph ${para.originalNumber}`);
                break;
              }
            }
            if (matchedParagraph) break;
          }
        }
      } else {
        console.log('[Selection] Skipping section search - activeTab:', activeTab, ', sections.length:', sections.length);
      }

      if (matchedSection && matchedParagraph) {
        // Use the paragraph's actual original number
        if (matchedParagraph.originalNumber > 0) {
          paragraphIndex = matchedParagraph.originalNumber;
        } else if (matchedSection.minParagraphNumber !== Infinity) {
          // Fallback: use section's min paragraph number
          paragraphIndex = matchedSection.minParagraphNumber;
        } else {
          paragraphIndex = 0;
        }

        textOffset = paragraphTextOffset;
        section = matchedSection.title || section;

        console.log('[Selection] Using paragraph originalNumber:', matchedParagraph.originalNumber);
        console.log('[Selection] Calculated actual paragraph index:', paragraphIndex);
      } else {
        // For other tabs (overview, claims, abstract), use actual DOM range position
        const allProseElements = Array.from(window.document.querySelectorAll('.prose, pre.whitespace-pre-wrap')) as HTMLElement[];
        console.log('[Selection] Searching', allProseElements.length, 'content areas for:', selectionText.substring(0, 50));

        // Get the actual DOM range
        const range = selection.getRangeAt(0);
        const container = range.startContainer;

        for (const element of allProseElements) {
          // Check if element is currently visible
          if (element.offsetParent === null) continue;

          // Check if this element contains the selection
          if (!element.contains(container)) continue;

          // Calculate the actual character offset from the start of element to the selection start
          const walker = window.document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            null
          );

          let currentOffset = 0;
          let foundStart = false;

          let node;
          while ((node = walker.nextNode())) {
            if (node === range.startContainer) {
              // Found the node containing the selection start
              textOffset = currentOffset + range.startOffset;
              foundStart = true;
              break;
            }
            // Add this node's text length to the offset
            currentOffset += (node.textContent || '').length;
          }

          if (foundStart) {
            paragraphIndex = 0; // For non-specification tabs, use 0
            console.log('[Selection] Found in', section, 'tab at exact offset:', textOffset);
            break;
          }
        }
      }

      console.log('[Selection] Final result:', { paragraphIndex, textOffset, section, text: text.substring(0, 50) });

      // Ensure we have valid values
      if (paragraphIndex === -1 && textOffset === 0 && activeTab === 'specification') {
        console.warn('[Selection] WARNING: Could not find paragraph/offset for specification selection!');
        console.warn('[Selection] Selected text:', text.substring(0, 100));
        console.warn('[Selection] Section:', section);
        console.warn('[Selection] Available sections:', sections.map(s => s.title));
      }
    }

    setSelectedText(text);
    setSelectionParagraphIndex(paragraphIndex);
    setSelectionSection(section);
    // Store the offset for precise matching later
    (window as any).__lastCommentOffset = textOffset;

    console.log('[Selection] Setting state:', {
      text: text.substring(0, 50),
      paragraphIndex,
      section,
      textOffset
    });

    setCommentPopupPosition({ x: e.clientX, y: e.clientY });
    setShowCommentPopup(true);
  }, [activeTab, getSections]); // Need activeTab to detect current tab

  const documentKey = useMemo(() => {
    if (!document) return '';
    const typePart = document.file_type || document.type || '';
    const idPart = document.resource_id || document.title || document.file_name || '';
    return `${typePart}:${idPart}`;
  }, [document]);

  useEffect(() => {
    if (!documentKey) return;
    if (lastLoadedDocKey.current === documentKey) return;
    lastLoadedDocKey.current = documentKey;
    loadDocument();
    setActiveTab('overview');
  }, [documentKey]);

  // Listen for text selection to show comment popup
  useEffect(() => {
    const handleMouseUp = (e: MouseEvent) => {
      // Only process if clicking within the content area
      const targetNode = e.target as Node;

      // Skip comment handling inside task history or task result viewer
      if (
        taskHistoryContainerRef.current?.contains(targetNode) ||
        taskResultViewerRef.current?.contains(targetNode)
      ) {
        return;
      }

      if (!contentRef.current?.contains(targetNode)) {
        return;
      }
      handleTextSelection(e);
    };

    window.document.addEventListener('mouseup', handleMouseUp);
    return () => window.document.removeEventListener('mouseup', handleMouseUp);
  }, [handleTextSelection]);

  // Watch for sessionApplications changes to update application number
  useEffect(() => {
    if (!document || !documentContent || !sessionApplications) return;

    const docType = document.file_type || document.type?.toLowerCase() || 'patent';
    if (docType !== 'patent' && docType !== 'publication') return;
    if (documentContent.application_number) return; // Already has application number

    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-)+/gi, '');
    const numberMatch = docId.match(/\d+/);
    if (numberMatch) {
      docId = numberMatch[0];
    }

    // Remove country code (US) and kind code (A1, B2, etc.) - keep only numbers
    // First remove US prefix, then remove kind code pattern (letter + 1-2 digits at end), then keep only numbers
    const cleanDocId = docId.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
    console.log('Watching for app (effect). Type:', docType, 'Original docId:', docId, '→ Clean:', cleanDocId, 'Total apps:', sessionApplications.length);

    const matchingApp = sessionApplications.find((app: any) => {
      const metaData = app.application?.applicationMetaData;
      if (!metaData) {
        console.log('  Skipping app (no metaData)');
        return false;
      }

      if (docType === 'patent' && metaData.patentNumber) {
        const patentNum = metaData.patentNumber.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
        console.log('  (effect) Checking patent:', metaData.patentNumber, '→', patentNum, 'vs', cleanDocId, 'match?', patentNum === cleanDocId);
        return patentNum === cleanDocId;
      } else if (docType === 'publication' && metaData.earliestPublicationNumber) {
        const pubNum = metaData.earliestPublicationNumber.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
        console.log('  (effect) Checking publication:', metaData.earliestPublicationNumber, '→', pubNum, 'vs', cleanDocId, 'match?', pubNum === cleanDocId);
        return pubNum === cleanDocId;
      } else {
        console.log('  Skipping app (no matching field for', docType, ')');
      }
      return false;
    });

    if (matchingApp) {
      const appNumber = matchingApp.application_id || matchingApp.application?.applicationNumberText;
      if (appNumber) {
        console.log('✓ Found matching application (effect):', appNumber, 'for document:', docId);
        setDocumentContent(prev => {
          // Only update if application_number actually changed
          if (prev && prev.application_number !== appNumber) {
            return { ...prev, application_number: appNumber };
          }
          return prev;
        });
      }
    } else {
      console.log('✗ No matching application found (effect) for:', docId);
    }
  }, [sessionApplications, document]);

  // Handle initial tab switch when component opens
  useEffect(() => {
    if (!documentContent || !initialTab) return;
    setActiveTab(initialTab);
  }, [initialTab, documentContent]);

  // Handle scroll to element after tab is active
  useEffect(() => {
    if (!documentContent || !scrollToId || !activeTab) return;

    // Retry mechanism to wait for DOM element to be available
    let attempts = 0;
    const maxAttempts = 15;
    const retryDelay = 300; // ms between attempts

    const attemptScroll = () => {
      const element = window.document.getElementById(scrollToId);
      if (element) {
        // Find the ScrollArea viewport (radix-ui uses data-radix-scroll-area-viewport)
        const scrollViewport = element.closest('[data-radix-scroll-area-viewport]');

        if (scrollViewport) {
          // Get element position relative to viewport
          const elementRect = element.getBoundingClientRect();
          const viewportRect = scrollViewport.getBoundingClientRect();
          const scrollTop = scrollViewport.scrollTop;
          const targetScroll = scrollTop + (elementRect.top - viewportRect.top) - (viewportRect.height / 2) + (elementRect.height / 2);

          // Smooth scroll the viewport
          scrollViewport.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          });
        } else {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Add a temporary highlight effect
        element.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 2000);
      } else {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(attemptScroll, retryDelay);
        }
      }
    };

    // Start attempting after initial delay for tab content to render
    const scrollTimer = setTimeout(attemptScroll, 1200);

    return () => clearTimeout(scrollTimer);
  }, [scrollToId, activeTab, documentContent, scrollTimestamp]);

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce claims filter text (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedClaimsFilterText(claimsFilterText);
    }, 300);
    return () => clearTimeout(timer);
  }, [claimsFilterText]);

  // Debounce reference numeral (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReferenceNumeral(referenceNumeral);
    }, 300);
    return () => clearTimeout(timer);
  }, [referenceNumeral]);

  // Reset search match count when search query changes
  useEffect(() => {
    setSearchMatchCount(0);
    setCurrentSearchIndex(0);
  }, [debouncedSearchQuery]);

  // Reset claims filter match count when claims filter changes
  useEffect(() => {
    setClaimsFilterMatchCount(0);
    setCurrentClaimsFilterIndex(0);
  }, [debouncedClaimsFilterText]);

  // Reset reference numeral match count when reference numeral changes
  useEffect(() => {
    setReferenceNumeralMatchCount(0);
    setCurrentReferenceNumeralIndex(0);
  }, [debouncedReferenceNumeral]);

  // Recalculate match counts when active tab changes
  useEffect(() => {
    // Wait a bit for the tab content to render
    const timer = setTimeout(() => {
      // Get only the currently active/visible tab content panel
      const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);

      if (!activeTabContent) {
        // Fallback: just query the whole document if we can't find the tab panel
        if (debouncedSearchQuery) {
          const matches = window.document.querySelectorAll('.search-match');
          setSearchMatchCount(matches.length);
          setCurrentSearchIndex(0);
        }
        if (debouncedReferenceNumeral) {
          const refMatches = window.document.querySelectorAll('.reference-numeral-match');
          setReferenceNumeralMatchCount(refMatches.length);
          setCurrentReferenceNumeralIndex(0);
        }
        if (claimsFilterEnabled && debouncedClaimsFilterText) {
          const claimsMatches = window.document.querySelectorAll('.claims-filter-match');
          setClaimsFilterMatchCount(claimsMatches.length);
        }
        return;
      }

      // Count search matches in the currently visible tab only
      if (debouncedSearchQuery) {
        const matches = activeTabContent.querySelectorAll('.search-match');
        setSearchMatchCount(matches.length);
        setCurrentSearchIndex(0);

        // Scroll to first match
        if (matches.length > 0) {
          matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
          matches.forEach((m, i) => {
            if (i === 0) {
              m.classList.add('ring-2', 'ring-blue-500');
            } else {
              m.classList.remove('ring-2', 'ring-blue-500');
            }
          });
        }
      }

      // Count reference numeral matches in the currently visible tab only
      if (debouncedReferenceNumeral) {
        const refMatches = activeTabContent.querySelectorAll('.reference-numeral-match');
        setReferenceNumeralMatchCount(refMatches.length);
        setCurrentReferenceNumeralIndex(0);

        // Scroll to first match
        if (refMatches.length > 0) {
          refMatches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
          refMatches.forEach((m, i) => {
            if (i === 0) {
              m.classList.add('ring-2', 'ring-blue-500');
            } else {
              m.classList.remove('ring-2', 'ring-blue-500');
            }
          });
        }
      }

      // Count claims filter matches if applicable
      if (claimsFilterEnabled && debouncedClaimsFilterText) {
        const claimsMatches = activeTabContent.querySelectorAll('.claims-filter-match');
        setClaimsFilterMatchCount(claimsMatches.length);
        setCurrentClaimsFilterIndex(0);
      }
    }, 150); // Delay to ensure tab content is rendered

    return () => clearTimeout(timer);
  }, [activeTab, debouncedSearchQuery, debouncedReferenceNumeral, debouncedClaimsFilterText, claimsFilterEnabled]);

  // Count all matches after DOM updates (only in active tab)
  useEffect(() => {
    if (!documentContent?.description) {
      return;
    }

    // Wait for DOM to update with highlighted content
    const timer = setTimeout(() => {
      // Get only the currently active/visible tab content panel
      const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);

      // Fallback to querying whole document if tab panel not found
      const searchContext = activeTabContent || window.document;

      if (debouncedSearchQuery.trim()) {
        const searchMatches = searchContext.querySelectorAll('.search-match');
        setSearchMatchCount(searchMatches.length);
      }

      if (debouncedClaimsFilterText.trim()) {
        const claimsMatches = searchContext.querySelectorAll('.claims-filter-match');
        setClaimsFilterMatchCount(claimsMatches.length);
      }

      if (debouncedReferenceNumeral.trim()) {
        const refMatches = searchContext.querySelectorAll('.reference-numeral-match');
        setReferenceNumeralMatchCount(refMatches.length);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [debouncedSearchQuery, debouncedClaimsFilterText, debouncedReferenceNumeral, documentContent, activeTab]);

  // Load drawings when sidebar is opened for patents/publications
  useEffect(() => {
    console.log('Drawings useEffect triggered! drawingsPinned:', drawingsPinned, 'document:', document);
    const loadDrawings = async () => {
      if (!drawingsPinned) {
        // Reset drawings state when sidebar is closed
        setDrawingsUrls([]);
        setSelectedDrawingIndex(null);
        setDrawingZoom(100);
        setDrawingRotation(0);
        setEpoDrawings([]);
        setEpoSelectedDrawing(null);
        setEpoCurrentPage(1);
        if (epoDrawingBlobUrl) { URL.revokeObjectURL(epoDrawingBlobUrl); setEpoDrawingBlobUrl(null); }
        return;
      }

      if (!document) {
        console.log('No document object found');
        return;
      }

      const docType = document.file_type || document.type || 'Document';
      console.log('Document type for drawings:', docType, 'Full document:', document);

      if (!['patent', 'publication', 'foreign_patent'].includes(docType.toLowerCase())) {
        console.log('Document type not patent, publication, or foreign_patent, skipping drawings');
        return;
      }

      // Get document ID
      let docId = document.resource_id || document.title || '';
      docId = docId.replace(/^(Patent|Publication|Application|Upload|Foreign Patent)\s+/i, '').trim();
      docId = docId.replace(/^(patent-|publication-|application-|foreign_patent-)+/gi, '');

      // For foreign patents, preserve the full ID with country code
      // For US patents, extract just the number
      if (docType.toLowerCase() !== 'foreign_patent') {
        const numberMatch = docId.match(/\d+/);
        if (numberMatch && docType !== 'upload') {
          docId = numberMatch[0];
        }
      }

      console.log('Loading drawings for:', { docType, docId, original: document.resource_id || document.title, fullDocument: document });

      setLoadingDrawings(true);
      try {
        const response = await documentApi.fetchDrawingsInfo(docType, docId);
        if (response && response.urls) {
          setDrawingsUrls(response.urls);
          if (response.urls.length > 0) {
            setSelectedDrawingIndex(null);
          }
        }
      } catch (error) {
        console.error('Error loading drawings:', error);
      } finally {
        setLoadingDrawings(false);
      }
    };

    loadDrawings();
  }, [drawingsPinned, document]);

  // Listen for mouseup in claims sidebar to capture completed selections
  useEffect(() => {
    const handleMouseUp = () => {
      setTimeout(() => {
        const selection = window.getSelection();
        const selectedText = selection?.toString().trim();

        if (selectedText && selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const container = range.commonAncestorContainer;

          // Check if selection is inside claims sidebar
          if (claimsSidebarRef.current && claimsSidebarRef.current.contains(container as Node)) {
            setClaimsFilterText(selectedText);
            setClaimsFilterEnabled(true);

            // Clear selection after a brief moment
            setTimeout(() => {
              if (window.getSelection) {
                window.getSelection()?.removeAllRanges();
              }
            }, 50);
          }
        }
      }, 10);
    };

    window.document.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Load EPO drawing page as blob URL
  const loadEpoPage = async (drawingIndex: number, page: number) => {
    const drawing = epoDrawings[drawingIndex];
    if (!drawing) return;
    let docId = document?.resource_id || document?.title || '';
    docId = docId.replace(/^(patent-|publication-|application-|foreign_patent-)+/gi, '');
    setEpoLoadingPage(true);
    if (epoDrawingBlobUrl) URL.revokeObjectURL(epoDrawingBlobUrl);
    setEpoDrawingBlobUrl(null);
    const url = await documentApi.fetchEpoDrawingPage(docId, drawing.link, page);
    setEpoDrawingBlobUrl(url);
    setEpoLoadingPage(false);
  };

  // Handle drawing viewer width resize
  useEffect(() => {
    if (!isResizingDrawingWidth) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!drawingViewerRef.current) return;

      const containerRect = drawingViewerRef.current.getBoundingClientRect();
      const newWidth = Math.max(200, Math.min(800, containerRect.right - e.clientX));
      setDrawingViewerWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizingDrawingWidth(false);
    };

    window.document.addEventListener('mousemove', handleMouseMove);
    window.document.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingDrawingWidth]);

  // Handle question panel width resize
  useEffect(() => {
    if (!isResizingQuestion) return;

    const handleMouseMove = (e: MouseEvent) => {
      const windowWidth = window.innerWidth;
      const mouseX = e.clientX;
      const minDocContentWidth = 570;
      const maxQuestionWidth = windowWidth - minDocContentWidth;
      const newWidth = Math.max(300, Math.min(maxQuestionWidth, windowWidth - mouseX));
      setQuestionWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizingQuestion(false);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };

    window.document.addEventListener('mousemove', handleMouseMove);
    window.document.addEventListener('mouseup', handleMouseUp);
    window.document.body.style.cursor = 'col-resize';
    window.document.body.style.userSelect = 'none';

    return () => {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };
  }, [isResizingQuestion]);

  // Handle task history panel width resize
  useEffect(() => {
    if (!isResizingTaskHistory) return;

    const handleMouseMove = (e: MouseEvent) => {
      const windowWidth = window.innerWidth;
      const mouseX = e.clientX;
      const minDocContentWidth = 570;
      const maxTaskHistoryWidth = windowWidth - minDocContentWidth;
      const newWidth = Math.max(300, Math.min(maxTaskHistoryWidth, windowWidth - mouseX));
      setTaskHistoryWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizingTaskHistory(false);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };

    window.document.addEventListener('mousemove', handleMouseMove);
    window.document.addEventListener('mouseup', handleMouseUp);
    window.document.body.style.cursor = 'col-resize';
    window.document.body.style.userSelect = 'none';

    return () => {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };
  }, [isResizingTaskHistory]);

  // Scroll management for question panel
  const scrollToTop = useCallback(() => {
    if (questionScrollRef.current) {
      questionScrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (questionScrollRef.current) {
      const { scrollHeight } = questionScrollRef.current;
      questionScrollRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  const checkScrollNeed = useCallback(() => {
    if (questionScrollRef.current) {
      const { scrollHeight, clientHeight } = questionScrollRef.current;
      if (scrollHeight >= 2 * clientHeight) {
        setShowTopButton(false);
        setShowBottomButton(true);
      } else {
        setShowTopButton(false);
        setShowBottomButton(false);
      }
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (questionScrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = questionScrollRef.current;
      setShowBottomButton(!(scrollTop + clientHeight >= scrollHeight - 10));
      setShowTopButton(!(scrollTop === 0));
    }
  }, []);

  // Toggle fullscreen for question history
  const toggleQuestionHistoryFullscreen = useCallback(() => {
    setShowQuestionHistoryFullscreen(!showQuestionHistoryFullscreen);
    // Scroll to bottom when opening fullscreen
    if (!showQuestionHistoryFullscreen) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [showQuestionHistoryFullscreen, scrollToBottom]);

  // Load question history when panel is opened
  const handleOpenQuestionHistory = useCallback(async () => {
    if (!document) return;

    const docType = (document.file_type || document.type || '').toLowerCase();
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-|upload-)+/gi, '');

    console.log('[QuestionHistory] Loading history for:', { docId, docType, documentRaw: document });

    try {
      const history = await documentApi.getCustomQuestionHistory(docId, docType);
      console.log('[QuestionHistory] Loaded history:', history);
      setQuestions(history);
      setShowQuestionHistory(true);
    } catch (error) {
      console.error('[QuestionHistory] Failed to load question history:', error);
      setShowQuestionHistory(true); // Still show panel even if loading fails
    }
  }, [document]);

  // Reload question history when document changes while panel is open
  useEffect(() => {
    if (showQuestionHistory && document) {
      const docType = (document.file_type || document.type || '').toLowerCase();
      let docId = document.resource_id || document.title || '';
      docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
      docId = docId.replace(/^(patent-|publication-|application-|upload-)+/gi, '');

      console.log('[QuestionHistory] Document changed, reloading history for:', { docId, docType });

      documentApi.getCustomQuestionHistory(docId, docType)
        .then(history => {
          console.log('[QuestionHistory] Reloaded history:', history);
          setQuestions(history);
        })
        .catch(error => {
          console.error('[QuestionHistory] Failed to reload question history:', error);
          setQuestions([]); // Clear questions on error
        });
    }
  }, [document, showQuestionHistory]);

  // Submit a custom question
  const handleQuestionSubmit = useCallback(async () => {
    if (!document || !questionText.trim() || questionLoading) return;

    const docType = (document.file_type || document.type || '').toLowerCase();
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-|upload-)+/gi, '');

    console.log('[QuestionHistory] Submitting question:', { question: questionText, docId, docType });

    setQuestionLoading(true);

    // Add optimistic UI update
    const newQuestion = {
      input_text: questionText,
      response: '...',
      timestamp: new Date().toISOString(),
    };
    setQuestions([...questions, newQuestion]);
    const currentQuestion = questionText;
    setQuestionText('');
    scrollToBottom();

    try {
      // Submit question
      const response = await documentApi.customQuestion(currentQuestion, docType, docId);
      console.log('[QuestionHistory] Question submitted, response:', response);

      if (response.error) {
        throw new Error(response.error);
      }

      const messageId = response.message_id;
      if (!messageId) {
        throw new Error('No message ID returned');
      }

      // Poll for response every 7.5 seconds
      const pollInterval = setInterval(async () => {
        try {
          console.log('[QuestionHistory] Polling for message:', messageId);
          const messageResponse = await chatApi.getMessage(messageId);
          console.log('[QuestionHistory] Poll response:', messageResponse);

          if (messageResponse.message &&
              messageResponse.message !== 'null' &&
              messageResponse.message !== null &&
              messageResponse.message !== undefined) {

            console.log('[QuestionHistory] Got response, refreshing history');
            // Refresh question history from backend
            const history = await documentApi.getCustomQuestionHistory(docId, docType);
            console.log('[QuestionHistory] History after response:', history);
            setQuestions(history);
            setQuestionLoading(false);
            scrollToBottom();
            clearInterval(pollInterval);
          }
        } catch (pollError) {
          console.error('[QuestionHistory] Polling error:', pollError);
        }
      }, 7500);

      // Clear interval after 5 minutes to prevent infinite polling
      setTimeout(() => {
        clearInterval(pollInterval);
        if (questionLoading) {
          setQuestionLoading(false);
          // Update the last question with error message
          setQuestions(prev => {
            const updated = [...prev];
            if (updated.length > 0) {
              updated[updated.length - 1].response = 'Error: Response timeout';
            }
            return updated;
          });
        }
      }, 300000); // 5 minutes

    } catch (error) {
      console.error('Failed to submit question:', error);
      setQuestionLoading(false);
      // Update the last question with error message
      setQuestions(prev => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1].response = 'Error generating response';
        }
        return updated;
      });
    }
  }, [document, questionText, questionLoading, questions, scrollToBottom]);

  // Fetch custom prompts on mount
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const response = await userApi.fetchPrompts();
        if (response.prompts) {
          setCustomPrompts(response.prompts);
        }
      } catch (error) {
        console.error('Failed to fetch custom prompts:', error);
      }
    };
    fetchPrompts();
  }, []);

  // Handle deleting a question
  const handleDeleteQuestion = useCallback(async (queryId: string, index: number) => {
    if (!document) return;

    const docType = (document.file_type || document.type || '').toLowerCase();
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-|upload-)+/gi, '');

    try {
      // Optimistically remove from UI
      setQuestions(prev => prev.filter((_, i) => i !== index));

      // Call backend to delete (if you have this API endpoint)
      // await documentApi.deleteCustomQuestion(queryId, docType, docId);
    } catch (error) {
      console.error('Failed to delete question:', error);
      // Reload questions on error
      const history = await documentApi.getCustomQuestionHistory(docId, docType);
      setQuestions(history);
    }
  }, [document]);

  // Handle creating/editing custom prompt
  const handleCreatePrompt = useCallback(() => {
    setEditingPrompt(null);
    setShowCustomPromptModal(true);
  }, []);

  // Handle saving custom prompt
  const handleSaveCustomPrompt = useCallback(async (label: string, prompt: string, availableIn: string) => {
    try {
      if (editingPrompt && editingPrompt.id) {
        // Update existing prompt
        await userApi.updatePrompt(editingPrompt.id, label, prompt, availableIn);
      } else {
        // Create new prompt
        await userApi.savePrompt(label, prompt, availableIn);
      }

      // Refresh prompts
      const response = await userApi.fetchPrompts();
      if (response.prompts) {
        setCustomPrompts(response.prompts);
      }

      setShowCustomPromptModal(false);
      setEditingPrompt(null);
    } catch (error) {
      console.error('Failed to save custom prompt:', error);
      throw error; // Let modal handle the error
    }
  }, [editingPrompt]);

  // Auto-scroll to bottom in fullscreen modal when questions change
  useEffect(() => {
    if (showQuestionHistoryFullscreen && questions.length > 0 && !questionLoading) {
      scrollToBottom();
    }
  }, [showQuestionHistoryFullscreen, questions.length, questionLoading, scrollToBottom]);

  // Helper functions for task result parsing and rendering
  const getResultData = (resultObj: any) => {
    if (!resultObj) return null;
    if (resultObj.result !== undefined) return resultObj.result;
    if (resultObj.output !== undefined) return resultObj.output;
    if (resultObj.analysis !== undefined) return resultObj.analysis;
    return resultObj;
  };

  const getParsedResult = (output: any) => {
    if (!output) return null;
    if (typeof output === 'object') return output;

    if (typeof output === 'string') {
      const trimmed = output.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return output;
      }

      try {
        return JSON.parse(output);
      } catch {
        if (output.includes("'")) {
          try {
            let jsonStr = output;

            // Step 1: Replace Python boolean/null literals
            jsonStr = jsonStr.replace(/\bTrue\b/g, 'true');
            jsonStr = jsonStr.replace(/\bFalse\b/g, 'false');
            jsonStr = jsonStr.replace(/\bNone\b/g, 'null');

            // Step 2: Protect double-quoted strings by replacing them with placeholders
            const doubleQuotedStrings: string[] = [];
            jsonStr = jsonStr.replace(/"([^"]*)"/g, (match, content) => {
              const index = doubleQuotedStrings.length;
              doubleQuotedStrings.push(match);
              return `__PLACEHOLDER_${index}__`;
            });

            // Step 3: Now convert single-quoted keys and values
            // Convert single-quoted keys to double-quoted
            jsonStr = jsonStr.replace(/'([^']*)':/g, '"$1":');

            // Convert single-quoted values (after colons)
            jsonStr = jsonStr.replace(/:\s*'([^']*)'/g, ': "$1"');

            // Convert single-quoted array elements
            jsonStr = jsonStr.replace(/\[\s*'([^']*)'/g, '["$1"');
            jsonStr = jsonStr.replace(/,\s*'([^']*)'/g, ', "$1"');
            jsonStr = jsonStr.replace(/'([^']*)']/g, '"$1"]');

            // Step 4: Restore double-quoted strings
            jsonStr = jsonStr.replace(/__PLACEHOLDER_(\d+)__/g, (match, index) => {
              return doubleQuotedStrings[parseInt(index)];
            });

            return JSON.parse(jsonStr);
          } catch (e) {
            console.warn('Failed to parse Python dict:', output.substring(0, 100) + '...', 'Error:', e);
            return output;
          }
        }
        console.warn('Failed to parse JSON:', output.substring(0, 100) + '...');
        return output;
      }
    }

    return output;
  };

  const renderTaskResultDisplay = (taskResult: TaskResultAttachment, compactButtons = true) => {
    const parsedData = getParsedResult(taskResult.content);
    // Hide header when in compact mode (inline view) since panel already has header
    const commonProps = {
      result: parsedData,
      theme: 'light' as const,
      showHeader: !compactButtons,
      compactButtons,
      showToast,
      onCopyResult: () => copyResult(parsedData, taskResult.taskType),
    };
    const taskType = taskResult.taskType;

    switch (taskType) {
      case 'parts_list':
        return <PartsListDisplay {...commonProps} showToggle={true} />;
      case 'spec_map':
        return <SpecMapDisplay {...commonProps} showToggle={true} />;
      case 'claims_support':
        return <ClaimsSupportDisplay {...commonProps} showToggle={true} />;
      case 'claim_chart':
        return <ClaimChartDisplay {...commonProps} />;
      case 'claims_with_numerals':
        return <ClaimsWithNumeralsDisplay {...commonProps} showToggle={true} />;
      case 'custom_analysis':
        return <CustomAnalysisDisplay {...commonProps} showToggle={true} />;
      case 'ir_metadata':
        return <IRMetadataDisplay {...commonProps} />;
      case 'ir_innovation':
        return <IRInnovationAnalysisDisplay {...commonProps} />;
      case 'ir_tech_classification':
        return <IRTechClassificationDisplay {...commonProps} />;
      case 'draft_summary':
        return <DraftSummaryDisplay {...commonProps} />;
      case 'draft_detailed_description':
        return <DraftDetailedDescriptionDisplay {...commonProps} />;
      case 'draft_background':
        return <DraftBackgroundDisplay {...commonProps} />;
      case 'draft_claims':
        return <DraftClaimsDisplay {...commonProps} />;
      case 'draft_other_embodiments':
        return <OtherEmbodimentsDisplay {...commonProps} />;
      case 'oa_extract':
        return <OAExtractDisplay {...commonProps} />;
      case 'oa_analyze':
        return <OAAnalyzeDisplay {...commonProps} />;
      case 'oa_summary_art':
        return <OASummaryArtDisplay {...commonProps} />;
      case 'oa_timeline':
        return <OATimelineDisplay {...commonProps} />;
      case 'translate_document':
        return <TranslateDocumentDisplay {...commonProps} />;
      default:
        return (
          <div className="p-4 rounded-lg bg-gray-50">
            <pre className="text-xs overflow-auto whitespace-pre-wrap">
              {typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2)}
            </pre>
          </div>
        );
    }
  };

  // Parse summary if it's JSON
  const _TOKEN_KEYS = new Set(['input_tokens', 'output_tokens', 'total_tokens', 'inputtokens', 'outputtokens']);

  const _unwrapOutput = (obj: Record<string, any>): Record<string, any> => {
    // If the object has an 'output' key that is itself an object, unwrap it
    if (obj.output && typeof obj.output === 'object' && !Array.isArray(obj.output)) {
      return obj.output as Record<string, any>;
    }
    // Strip token metadata keys that add no display value
    const filtered = Object.fromEntries(
      Object.entries(obj).filter(([k]) => !_TOKEN_KEYS.has(k.toLowerCase()))
    );
    return filtered;
  };

  const parseSummary = (summary: string | object | undefined): string | object | null => {
    if (!summary) return null;

    if (typeof summary === 'string') {
      const trimmed = summary.trim();

      // "unknown" is the backend's placeholder for a summary that has not been
      // generated yet (ddb.get_document_summary returns it when the attribute is
      // absent). It is truthy and it is not JSON, so it used to fall through this
      // function unchanged and render under an "AI Summary" heading as the literal
      // word "unknown" -- while the document was still being onboarded and the
      // spinner for it was still turning in the documents list.
      if (trimmed === 'unknown' || trimmed === '') return null;

      // 1. Try standard JSON parse first
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return _unwrapOutput(parsed as Record<string, any>);
        }
        return parsed;
      } catch {
        // not valid JSON
      }

      if (trimmed.startsWith("{")) {
        // 2. Try converting Python-style dict (single-quoted keys) to JSON.
        //    Handles: {'technical_problem': "...", 'technical_solution': "..."}
        //    and:     {'output': {...}, 'input_tokens': 123, ...}
        try {
          const jsonLike = trimmed
            .replace(/'([^']+)'(\s*:)/g, '"$1"$2') // 'key': → "key":
            .replace(/:\s*'([^']*)'/g, ': "$1"')   // : 'value' → : "value"
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false');
          const parsed = JSON.parse(jsonLike);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return _unwrapOutput(parsed as Record<string, any>);
          }
          return parsed;
        } catch {
          // conversion failed
        }

        // 3. Try to extract 'output' string value from Python dict format:
        //    {'output': '...text...', 'input_tokens': 123, ...}
        if (trimmed.includes("'output'")) {
          const outputMatch = trimmed.match(/'output':\s*'([\s\S]+)',\s*'(?:input_tokens|inputtokens)':/);
          if (outputMatch) {
            return { output: outputMatch[1].replace(/\\'/g, "'").replace(/\\n/g, "\n") };
          }
        }
      }

      return summary;
    }

    if (typeof summary === 'object') {
      return _unwrapOutput(summary as Record<string, any>);
    }

    return String(summary);
  };

  // Get claims array
  const getClaims = () => {
    if (!documentContent?.claims) return [];

    // Handle string format (HTML) - don't parse, return empty for now
    // Claims will be shown in their own tab if they exist as string
    if (typeof documentContent.claims === 'string') {
      return [];
    }

    let claimsArray: Array<{ claim_number: string; text: string; dependencies?: string[] }> = [];

    // Handle array format (patents)
    if (Array.isArray(documentContent.claims)) {
      claimsArray = documentContent.claims.map(claim => ({
        ...claim,
        claim_number: String(claim.claim_number || '').replace(/^0+/, '') // Remove leading zeros
      }));
    }
    // Handle dictionary format (foreign patents)
    else if (typeof documentContent.claims === 'object') {
      claimsArray = Object.entries(documentContent.claims as Record<string, any>)
        .map(([key, value]) => ({
          claim_number: String(value.claim_number || key).replace(/^0+/, ''),
          text: value.claim_text || value.text || String(value),
          dependencies: value.dependencies || []
        }));
    }

    // Sort claims by claim_number in ascending numerical order
    return claimsArray.sort((a, b) => {
      const numA = parseInt(a.claim_number) || 0;
      const numB = parseInt(b.claim_number) || 0;
      return numA - numB;
    });
  };

  const documentName = document?.file_name || document?.title || document?.resource_id || 'Document';
  const documentType = document?.file_type || document?.type || 'Document';

  // Get clean doc ID for comments
  const getDocId = () => {
    let docId = document?.resource_id || document?.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Foreign Patent|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-|foreign_patent-)+/gi, '');
    const numberMatch = docId.match(/\d+/);
    if (numberMatch && documentType.toLowerCase() !== 'upload' && documentType.toLowerCase() !== 'foreign_patent') {
      docId = numberMatch[0];
    }
    return docId;
  };

  // Load comments when document changes
  useEffect(() => {
    const loadComments = async () => {
      const docId = getDocId();
      const docType = documentType.toLowerCase();
      console.log('[DocumentViewer] Loading comments for:', { docType, docId, resourceId: document?.resource_id });
      if (docId && docType) {
        const loadedComments = await documentApi.getDocumentComments(docType, docId);
        console.log('[DocumentViewer] Loaded comments:', loadedComments);
        setComments(loadedComments);
      }
    };
    loadComments();
  }, [document?.resource_id, documentType]);

  // Highlight commented text in the document
  useEffect(() => {
    console.log('[Highlighting] Effect triggered. Comments:', comments.length, 'documentContent:', !!documentContent);
    if (comments.length === 0) {
      console.log('[Highlighting] Skipping - no comments');
      return;
    }

    if (!documentContent) {
      console.log('[Highlighting] Skipping - no documentContent yet');
      return;
    }

    const highlightCommentedText = () => {
      // Search in ALL document areas (all tabs) - not just the currently visible one
      const allProseElements = Array.from(window.document.querySelectorAll('.prose, pre.whitespace-pre-wrap')) as HTMLElement[];

      console.log('[Highlighting] Found', allProseElements.length, 'potential content areas');

      // Check if content is actually rendered
      const hasContent = allProseElements.some(el => (el.textContent?.length || 0) > 100);
      if (!hasContent) {
        console.log('[Highlighting] Content not rendered yet, retrying...');
        setTimeout(() => {
          setComments([...comments]); // Trigger re-highlight
        }, 500);
        return;
      }
      console.log('[Highlighting] Starting highlighting for', comments.length, 'comments');

      // Remove existing comment highlights from all areas
      allProseElements.forEach(element => {
        const existingHighlights = element.querySelectorAll('.comment-highlight');
        existingHighlights.forEach(el => {
          const parent = el.parentNode;
          if (parent) {
            parent.replaceChild(window.document.createTextNode(el.textContent || ''), el);
            parent.normalize();
          }
        });
      });

      // Add highlights for each comment using exact paragraph index and offset
      comments.forEach((comment, idx) => {
        const text = comment.selected_text;
        console.log('[Highlighting] Processing comment', idx, ':', text.substring(0, 50), 'section:', comment.section, 'paragraph:', comment.paragraph_index, 'offset:', comment.text_offset);
        if (!text || text.length < 3) return;

        // Variables to track where we found the text
        let foundInElement: HTMLElement | null = null;
        let exactStartOffset = -1;

        // Check if this is a specification comment (has section title like "BACKGROUND OF THE INVENTION")
        const isSpecificationComment = comment.section !== 'overview' &&
                                      comment.section !== 'claims' &&
                                      comment.section !== 'abstract' &&
                                      comment.section !== 'content';

        if (isSpecificationComment) {
          // Find the specific paragraph using paragraph_index and section
          const sections = getSections();
          let targetParagraph: any = null;

          // Find the section and paragraph that matches this comment
          for (const sec of sections) {
            if (sec.title === comment.section) {
              // Find the paragraph with matching originalNumber
              targetParagraph = sec.paragraphs.find((p: any) => p.originalNumber === comment.paragraph_index);
              if (targetParagraph) {
                console.log('[Highlighting] Found target paragraph', comment.paragraph_index, 'in section', comment.section);
                break;
              }
            }
          }

          if (!targetParagraph || !targetParagraph.content) {
            console.log('[Highlighting] Target paragraph not found');
            return;
          }

          const paragraphText = targetParagraph.content;

          // Use the stored text_offset to find the exact position
          const commentOffset = comment.text_offset || 0;

          // Verify the text at this offset matches (allowing for some whitespace variation)
          const textAtOffset = paragraphText.substring(commentOffset, commentOffset + text.length);
          const normalizedTextAtOffset = textAtOffset.replace(/\s+/g, ' ').trim().toLowerCase();
          const normalizedCommentText = text.replace(/\s+/g, ' ').trim().toLowerCase();

          if (normalizedTextAtOffset !== normalizedCommentText) {
            console.log('[Highlighting] Text mismatch at stored offset. Expected:', text.substring(0, 30), 'Found:', textAtOffset.substring(0, 30));
            // Still try to use the offset as starting point but search nearby
            const searchStart = Math.max(0, commentOffset - 50);
            const searchEnd = Math.min(paragraphText.length, commentOffset + text.length + 50);
            const searchArea = paragraphText.substring(searchStart, searchEnd);
            const normalizedSearchArea = searchArea.replace(/\s+/g, ' ').toLowerCase();
            const localOffset = normalizedSearchArea.indexOf(normalizedCommentText);

            if (localOffset === -1) {
              console.log('[Highlighting] Text not found near stored offset');
              return;
            }

            exactStartOffset = searchStart + localOffset;
          } else {
            exactStartOffset = commentOffset;
          }

          // Now find this paragraph's text in the DOM using a unique snippet
          const uniqueSnippet = paragraphText.substring(0, Math.min(50, paragraphText.length));

          for (const element of allProseElements) {
            const allText = element.textContent || '';
            if (allText.includes(uniqueSnippet)) {
              foundInElement = element;
              console.log('[Highlighting] Found paragraph in DOM element:', element.className);
              break;
            }
          }

          if (!foundInElement) {
            console.log('[Highlighting] Paragraph not found in DOM');
            return;
          }

          // Now we need to find the exact position in the DOM that corresponds to exactStartOffset in paragraphText
          // Build continuous text from the element and find where our paragraph starts
          const elementText = foundInElement.textContent || '';
          const paragraphStartInElement = elementText.indexOf(paragraphText.substring(0, 100));

          if (paragraphStartInElement === -1) {
            console.log('[Highlighting] Could not locate paragraph start in element');
            return;
          }

          // The actual offset in the element's text is: paragraph start + offset within paragraph
          const absoluteOffset = paragraphStartInElement + exactStartOffset;

          console.log('[Highlighting] Using exact offset:', absoluteOffset, 'in element (paragraph starts at', paragraphStartInElement, '+ offset', exactStartOffset, ')');

          // Use TreeWalker to build text nodes and find the exact position
          const walker = window.document.createTreeWalker(
            foundInElement,
            NodeFilter.SHOW_TEXT,
            null
          );

          const textNodes: { node: Node; text: string }[] = [];
          let node;
          while ((node = walker.nextNode())) {
            const nodeText = node.textContent || '';
            if (nodeText.length > 0) {
              textNodes.push({ node, text: nodeText });
            }
          }

          // Build continuous text and track node positions
          let continuousText = '';
          const nodePositions: { node: Node; start: number; end: number; originalText: string }[] = [];

          textNodes.forEach(({ node, text }) => {
            const start = continuousText.length;
            continuousText += text;
            const end = continuousText.length;
            nodePositions.push({ node, start, end, originalText: text });
          });

          // Find nodes that contain our exact range
          const highlightEnd = absoluteOffset + text.length;
          const nodesToReplace: { node: Node; start: number; end: number }[] = [];

          nodePositions.forEach(({ node, start, end, originalText }) => {
            // Check if this node overlaps with the exact range
            if (start < highlightEnd && end > absoluteOffset) {
              const nodeStart = Math.max(0, absoluteOffset - start);
              const nodeEnd = Math.min(originalText.length, highlightEnd - start);
              nodesToReplace.push({ node, start: nodeStart, end: nodeEnd });
            }
          });

          console.log('[Highlighting] Highlighting spans', nodesToReplace.length, 'text nodes');

          // Replace text nodes with highlighted spans
          nodesToReplace.forEach(({ node, start, end }) => {
            const nodeText = node.textContent || '';
            const before = nodeText.substring(0, start);
            const match = nodeText.substring(start, end);
            const after = nodeText.substring(end);

            const span = window.document.createElement('span');
            span.className = 'comment-highlight bg-yellow-100 hover:bg-yellow-200 cursor-pointer border-b-2 border-yellow-400 transition-colors';
            span.textContent = match;
            span.dataset.commentId = comment.id;
            span.title = comment.comment_text;

            // Add click handler to open comments panel
            span.addEventListener('click', () => {
              setShowCommentsPanel(true);
            });

            const fragment = window.document.createDocumentFragment();
            if (before) fragment.appendChild(window.document.createTextNode(before));
            fragment.appendChild(span);
            if (after) fragment.appendChild(window.document.createTextNode(after));

            node.parentNode?.replaceChild(fragment, node);
            console.log('[Highlighting] Added highlight span for comment', idx);
          });
        } else {
          // For tab-level comments (overview, claims, abstract, content)
          // Use text_offset to find exact position in the tab content
          console.log('[Highlighting] Tab-level comment for section:', comment.section, 'offset:', comment.text_offset);

          // Search in the current content - should only be visible if on the right tab
          for (const element of allProseElements) {
            // Check if element is currently visible (simple heuristic)
            if (element.offsetParent === null) continue; // Skip hidden elements

            const allText = element.textContent || '';

            // Use the stored text_offset
            const commentOffset = comment.text_offset || 0;

            // Verify the text at this offset matches
            const textAtOffset = allText.substring(commentOffset, commentOffset + text.length);
            const normalizedTextAtOffset = textAtOffset.replace(/\s+/g, ' ').trim().toLowerCase();
            const normalizedCommentText = text.replace(/\s+/g, ' ').trim().toLowerCase();

            if (normalizedTextAtOffset === normalizedCommentText) {
              foundInElement = element;
              exactStartOffset = commentOffset;
              console.log('[Highlighting] Found at exact offset', commentOffset, 'in visible element:', element.className);
              break;
            }
          }

          if (!foundInElement || exactStartOffset === -1) {
            console.log('[Highlighting] Text not found at stored offset in any visible element');
            return;
          }

          // Use TreeWalker to find exact position
          const walker = window.document.createTreeWalker(
            foundInElement,
            NodeFilter.SHOW_TEXT,
            null
          );

          const textNodes: { node: Node; text: string }[] = [];
          let node;
          while ((node = walker.nextNode())) {
            const nodeText = node.textContent || '';
            if (nodeText.length > 0) {
              textNodes.push({ node, text: nodeText });
            }
          }

          // Build continuous text and track node positions
          let continuousText = '';
          const nodePositions: { node: Node; start: number; end: number; originalText: string }[] = [];

          textNodes.forEach(({ node, text }) => {
            const start = continuousText.length;
            continuousText += text;
            const end = continuousText.length;
            nodePositions.push({ node, start, end, originalText: text });
          });

          // Find nodes that contain our exact range
          const highlightEnd = exactStartOffset + text.length;
          const nodesToReplace: { node: Node; start: number; end: number }[] = [];

          nodePositions.forEach(({ node, start, end, originalText }) => {
            // Check if this node overlaps with the exact range
            if (start < highlightEnd && end > exactStartOffset) {
              const nodeStart = Math.max(0, exactStartOffset - start);
              const nodeEnd = Math.min(originalText.length, highlightEnd - start);
              nodesToReplace.push({ node, start: nodeStart, end: nodeEnd });
            }
          });

          console.log('[Highlighting] Highlighting spans', nodesToReplace.length, 'text nodes');

          // Replace text nodes with highlighted spans
          nodesToReplace.forEach(({ node, start, end }) => {
            const nodeText = node.textContent || '';
            const before = nodeText.substring(0, start);
            const match = nodeText.substring(start, end);
            const after = nodeText.substring(end);

            const span = window.document.createElement('span');
            span.className = 'comment-highlight bg-yellow-100 hover:bg-yellow-200 cursor-pointer border-b-2 border-yellow-400 transition-colors';
            span.textContent = match;
            span.dataset.commentId = comment.id;
            span.title = comment.comment_text;

            // Add click handler to open comments panel
            span.addEventListener('click', () => {
              setShowCommentsPanel(true);
            });

            const fragment = window.document.createDocumentFragment();
            if (before) fragment.appendChild(window.document.createTextNode(before));
            fragment.appendChild(span);
            if (after) fragment.appendChild(window.document.createTextNode(after));

            node.parentNode?.replaceChild(fragment, node);
            console.log('[Highlighting] Added highlight span for comment', idx);
          });
        }
      });
    };

    // Delay to ensure DOM is fully rendered (especially after dangerouslySetInnerHTML and markdown rendering)
    const timer = setTimeout(() => {
      highlightCommentedText();
    }, 1000); // Increased delay to ensure tab content and markdown are rendered

    return () => clearTimeout(timer);
  }, [comments, documentContent, activeTab]); // Re-highlight when switching tabs

  // Add comment
  const addComment = async () => {
    if (!newCommentText.trim() || !selectedText) return;

    const textOffset = (window as any).__lastCommentOffset || 0;

    console.log('[AddComment] Creating comment with:', {
      paragraph_index: selectionParagraphIndex,
      text_offset: textOffset,
      section: selectionSection,
      selected_text: selectedText.substring(0, 50)
    });

    const newComment: DocumentComment = {
      id: crypto.randomUUID(),
      paragraph_index: selectionParagraphIndex ?? -1,
      selected_text: selectedText,
      comment_text: newCommentText,
      section: selectionSection,
      text_offset: textOffset,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    console.log('[AddComment] Final comment object:', {
      paragraph_index: newComment.paragraph_index,
      text_offset: newComment.text_offset,
      section: newComment.section
    });

    const updatedComments = [...comments, newComment];
    setComments(updatedComments);

    // Save to backend
    const docId = getDocId();
    const docType = documentType.toLowerCase();
    console.log('[DocumentViewer] Saving comments for:', { docType, docId, resourceId: document?.resource_id, numComments: updatedComments.length });
    const success = await documentApi.saveDocumentComments(docType, docId, updatedComments);

    if (success) {
      addNotification({ type: 'success', title: 'Comment Added', message: 'Your comment has been saved.', duration: 3000 });
    } else {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to save comment.', duration: 3000 });
    }

    setNewCommentText('');
    setShowCommentPopup(false);
    setSelectedText('');

    // Force re-render to apply highlighting after saving comment
    // Use a small delay to ensure DOM is updated
    setTimeout(() => {
      setComments([...updatedComments]);
    }, 100);
  };

  // Update comment
  const updateComment = async (commentId: string) => {
    if (!editingCommentText.trim()) return;

    const updatedComments = comments.map(c =>
      c.id === commentId
        ? { ...c, comment_text: editingCommentText, updated_at: new Date().toISOString() }
        : c
    );
    setComments(updatedComments);

    const docId = getDocId();
    const docType = documentType.toLowerCase();
    await documentApi.saveDocumentComments(docType, docId, updatedComments);

    setEditingCommentId(null);
    setEditingCommentText('');
    addNotification({ type: 'success', title: 'Comment Updated', message: 'Your comment has been updated.', duration: 3000 });

    // Force re-render to update highlighting
    setTimeout(() => {
      setComments([...updatedComments]);
    }, 100);
  };

  // Delete comment
  const deleteComment = async (commentId: string) => {
    const updatedComments = comments.filter(c => c.id !== commentId);
    setComments(updatedComments);

    const docId = getDocId();
    const docType = documentType.toLowerCase();
    await documentApi.saveDocumentComments(docType, docId, updatedComments);
    addNotification({ type: 'info', title: 'Comment Deleted', message: 'Your comment has been removed.', duration: 3000 });

    // Force re-render to remove highlighting
    setTimeout(() => {
      setComments([...updatedComments]);
    }, 100);
  };

  console.log('DocumentViewer render - documentType:', documentType, 'document:', document);

  const summary = parseSummary(documentContent?.summary);
  const sections = useMemo(() => getSections(), [getSections]);
  const claims = useMemo(() => getClaims(), [getClaims]);

  // Process description with highlighting
  const processedDescription = useMemo(() => {
    console.log('=== processedDescription useMemo ===');
    console.log('documentContent.description type:', typeof documentContent?.description);

    if (!documentContent?.description) {
      console.log('[processedDescription] No description');
      return '';
    }

    // Check if description is a string (HTML) or object (structured sections)
    if (typeof documentContent.description !== 'string') {
      console.log('[processedDescription] Description is not a string, returning empty');
      return ''; // Return empty string if it's structured - will use sections rendering instead
    }

    const descString = documentContent.description;
    console.log('[processedDescription] Description string length:', descString.length);

    // Check if it looks like HTML (contains HTML tags)
    const hasHTMLTags = /<[a-z][\s\S]*>/i.test(descString);
    console.log('[processedDescription] Has HTML tags?:', hasHTMLTags);

    // If it's plain text without HTML tags, wrap it in paragraph tags for display
    let htmlContent: string;
    if (!hasHTMLTags) {
      console.log('[processedDescription] Plain text detected, wrapping in paragraphs');
      // Split by double newlines for paragraphs, or single newlines if no doubles
      const paragraphs = descString.includes('\n\n')
        ? descString.split('\n\n')
        : descString.split('\n');

      htmlContent = paragraphs
        .filter(p => p.trim())
        .map(p => `<p>${p.trim()}</p>`)
        .join('\n');
    } else {
      console.log('[processedDescription] HTML detected, proceeding with reordering');
      htmlContent = descString;
    }

    // Continue to highlighting logic below (don't return early)

    // Parse and reorder sections by paragraph number
    const reorderSections = (htmlContent: string): string => {
      console.log('=== reorderSections called ===');
      console.log('HTML content length:', htmlContent.length);
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');

      // Find all elements
      const allElements = Array.from(doc.body.children);

      // Create array of elements with their sort numbers
      const elementsWithNumbers: Array<{ element: Element; sortNum: number | null }> = [];

      allElements.forEach((elem, idx) => {
        const elemText = elem.textContent || '';
        // Look for paragraph numbers like [0001], [0030], etc.
        const paraMatch = elemText.match(/\[(\d{4})\]/) || elemText.match(/\[(\d{1,3})\]/);

        let sortNum: number | null = null;

        if (paraMatch) {
          sortNum = parseInt(paraMatch[1]);

          // Handle [0000] - hide the number but keep the element
          if (sortNum === 0) {
            const walker = window.document.createTreeWalker(elem, NodeFilter.SHOW_TEXT, null);
            let node;
            while ((node = walker.nextNode())) {
              const text = node.textContent || '';
              if (text.match(/\[0{4}\]/) || text.match(/\[0\]/)) {
                node.textContent = text.replace(/\[0{4}\]\s*/, '').replace(/\[0\]\s*/, '');
                break;
              }
            }
            sortNum = null; // Treat as no number for sorting
          }
        } else {
          // Element has no paragraph number - check if it's a section heading
          const isBoldHeading = elem.classList.contains('font-bold') ||
                               elem.querySelector('.font-bold') !== null ||
                               elem.tagName === 'H1' || elem.tagName === 'H2' || elem.tagName === 'H3';

          if (isBoldHeading) {
            // Look ahead to find the next element with a paragraph number
            for (let i = idx + 1; i < allElements.length; i++) {
              const nextElemText = allElements[i].textContent || '';
              const nextParaMatch = nextElemText.match(/\[(\d{4})\]/) || nextElemText.match(/\[(\d{1,3})\]/);

              if (nextParaMatch) {
                const nextParaNum = parseInt(nextParaMatch[1]);
                if (nextParaNum > 0) {
                  // Use this paragraph number minus 0.5 to ensure heading sorts just before its section
                  sortNum = nextParaNum - 0.5;
                  break;
                }
              }
            }
          }
        }

        elementsWithNumbers.push({ element: elem, sortNum });
      });

      // Separate elements with sort numbers from those without
      const withNumbers = elementsWithNumbers.filter(item => item.sortNum !== null);
      const withoutNumbers = elementsWithNumbers.filter(item => item.sortNum === null);

      // Debug logging
      console.log('Paragraph numbers found:', withNumbers.map(item => item.sortNum).sort((a, b) => a! - b!));
      console.log('Total elements with numbers:', withNumbers.length);
      console.log('Total elements without numbers:', withoutNumbers.length);

      // Sort elements that have sort numbers
      withNumbers.sort((a, b) => (a.sortNum! - b.sortNum!));

      // Rebuild HTML: First, elements with numbers (sorted), then elements without numbers
      const newBody = window.document.createElement('div');

      withNumbers.forEach(item => {
        newBody.appendChild(item.element.cloneNode(true));
      });

      withoutNumbers.forEach(item => {
        newBody.appendChild(item.element.cloneNode(true));
      });

      return newBody.innerHTML;
    };

    const highlightSearchQuery = (htmlContent: string) => {
      const query = debouncedSearchQuery.trim();
      const claimsFilter = debouncedClaimsFilterText.trim();

      // Check if we have any highlights to apply (search, claimsFilter, or pinned)
      if (!query && !claimsFilter && pinnedHighlights.length === 0) {
        return { html: htmlContent, searchCount: 0 };
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      let searchCount = 0;

      const highlightTextNodes = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          const lowerText = text.toLowerCase();

          const hasQuery = query && lowerText.includes(query.toLowerCase());
          const hasClaimsFilter = claimsFilter && lowerText.includes(claimsFilter.toLowerCase());
          const hasPinnedMatch = pinnedHighlights.some(p => lowerText.includes(p.term.toLowerCase()));

          if (!hasQuery && !hasClaimsFilter && !hasPinnedMatch) {
            return;
          }

          // Unified highlighting approach - collect all matches
          const matches: Array<{ start: number; end: number; type: string; color: string; cssColor?: string; text: string }> = [];

          // Find all query matches
          if (query) {
            const lowerQuery = query.toLowerCase();
            let pos = 0;
            while ((pos = lowerText.indexOf(lowerQuery, pos)) !== -1) {
              matches.push({ start: pos, end: pos + query.length, type: 'search', color: 'bg-yellow-300', text: text.substring(pos, pos + query.length) });
              pos++;
            }
          }

          // Find all claims filter matches
          if (claimsFilter) {
            const lowerClaimsFilter = claimsFilter.toLowerCase();
            let pos = 0;
            while ((pos = lowerText.indexOf(lowerClaimsFilter, pos)) !== -1) {
              matches.push({ start: pos, end: pos + claimsFilter.length, type: 'claims', color: 'bg-green-300', text: text.substring(pos, pos + claimsFilter.length) });
              pos++;
            }
          }

          // Find all pinned highlight matches
          pinnedHighlights.forEach(pinned => {
            const lowerTerm = pinned.term.toLowerCase();
            let pos = 0;
            while ((pos = lowerText.indexOf(lowerTerm, pos)) !== -1) {
              matches.push({ start: pos, end: pos + pinned.term.length, type: 'pinned', color: pinned.color, cssColor: pinned.cssColor, text: text.substring(pos, pos + pinned.term.length) });
              pos++;
            }
          });

          if (matches.length === 0) return;

          // Sort by length (longer first), then by start position
          matches.sort((a, b) => {
            const lenDiff = (b.end - b.start) - (a.end - a.start);
            if (lenDiff !== 0) return lenDiff;
            return a.start - b.start;
          });

          // Build non-overlapping ranges (search/claims take priority over pinned)
          const usedRanges: Array<{ start: number; end: number }> = [];
          const finalMatches: typeof matches = [];
          matches.forEach(match => {
            const overlaps = usedRanges.some(range =>
              (match.start >= range.start && match.start < range.end) ||
              (match.end > range.start && match.end <= range.end) ||
              (match.start <= range.start && match.end >= range.end)
            );

            if (!overlaps) {
              usedRanges.push({ start: match.start, end: match.end });
              finalMatches.push(match);
            }
          });

          finalMatches.sort((a, b) => a.start - b.start);

          // Build highlighted text
          const highlightParts: Node[] = [];
          let lastEnd = 0;

          finalMatches.forEach(match => {
            if (match.start > lastEnd) {
              highlightParts.push(window.document.createTextNode(text.substring(lastEnd, match.start)));
            }

            const mark = window.document.createElement('mark');
            if (match.type === 'search') {
              searchCount++;
              mark.className = 'search-match';
              mark.style.backgroundColor = '#fde047'; // yellow-300
              mark.style.color = 'black';
              mark.setAttribute('data-match-index', String(searchCount - 1));
            } else if (match.type === 'claims') {
              mark.className = 'claims-filter-match';
              mark.style.backgroundColor = '#86efac'; // green-300
              mark.style.color = 'black';
            } else {
              mark.className = 'pinned-highlight';
              mark.style.backgroundColor = match.cssColor || '#f9a8d4'; // Use inline style with CSS color
              mark.style.color = 'black';
            }
            mark.textContent = match.text;
            highlightParts.push(mark);
            lastEnd = match.end;
          });

          if (lastEnd < text.length) {
            highlightParts.push(window.document.createTextNode(text.substring(lastEnd)));
          }

          const span = window.document.createElement('span');
          highlightParts.forEach(part => span.appendChild(part));
          if (node.parentNode) {
            node.parentNode.replaceChild(span, node);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          if (element.tagName !== 'MARK') {
            Array.from(node.childNodes).forEach(child => highlightTextNodes(child));
          }
        }
      };

      highlightTextNodes(doc.body);
      return { html: doc.body.innerHTML, searchCount };
    };

    const filterDescriptionByMatches = (htmlContent: string) => {
      const hasSearchFilter = filterSearchResults && debouncedSearchQuery.trim();
      const hasClaimsFilter = claimsFilterEnabled && debouncedClaimsFilterText.trim();

      if (!hasSearchFilter && !hasClaimsFilter) return htmlContent;

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const allElements = doc.body.querySelectorAll('p, div');

      allElements.forEach(elem => {
        let shouldShow = false;

        if (hasSearchFilter || hasClaimsFilter) {
          const searchMarks = hasSearchFilter ? elem.querySelectorAll('.search-match') : [];
          const claimsMarks = hasClaimsFilter ? elem.querySelectorAll('.claims-filter-match') : [];

          if (searchMarks.length > 0 || claimsMarks.length > 0) {
            shouldShow = true;
          }
        }

        const isHeading = elem.classList.contains('font-bold');

        if (isHeading) {
          let nextElem = elem.nextElementSibling;
          let foundInSection = false;
          while (nextElem && !nextElem.classList.contains('font-bold')) {
            const searchMarksInNext = hasSearchFilter ? nextElem.querySelectorAll('.search-match') : [];
            const claimsMarksInNext = hasClaimsFilter ? nextElem.querySelectorAll('.claims-filter-match') : [];

            if (searchMarksInNext.length > 0 || claimsMarksInNext.length > 0) {
              foundInSection = true;
            }
            nextElem = nextElem.nextElementSibling;
          }
          if (!foundInSection) {
            (elem as HTMLElement).style.display = 'none';
          }
        } else if (!shouldShow) {
          (elem as HTMLElement).style.display = 'none';
        }
      });

      return doc.body.innerHTML;
    };

    // First reorder sections (only for HTML content), then apply highlighting
    const reordered = hasHTMLTags ? reorderSections(htmlContent) : htmlContent;
    const highlighted = highlightSearchQuery(reordered);
    const filtered = filterDescriptionByMatches(highlighted.html);

    // Remove [0000] paragraph numbers
    const cleanedFiltered = filtered.replace(/\[0{4}\]\s*/g, '').replace(/\[0\]\s*/g, '');

    // Debug logging - check browser console
    console.log('[DocumentViewer] Highlighting debug:', {
      pinnedCount: pinnedHighlights.length,
      pinnedTerms: pinnedHighlights.map(p => p.term),
      searchQuery: debouncedSearchQuery,
      hasPinnedMarks: cleanedFiltered.includes('pinned-highlight'),
      hasSearchMarks: cleanedFiltered.includes('search-match'),
    });

    // Update search match count
    if (highlighted.searchCount !== searchMatchCount) {
      setTimeout(() => {
        setSearchMatchCount(highlighted.searchCount);
        if (highlighted.searchCount > 0) {
          setTimeout(() => {
            const matches = descriptionRef.current?.querySelectorAll('.search-match');
            if (matches && matches[0]) {
              matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
              matches.forEach((m, i) => {
                if (i === 0) {
                  m.classList.add('ring-2', 'ring-blue-500');
                } else {
                  m.classList.remove('ring-2', 'ring-blue-500');
                }
              });
            }
          }, 100);
        }
      }, 0);
    }

    return cleanedFiltered;
  }, [documentContent?.description, debouncedSearchQuery, debouncedClaimsFilterText, claimsFilterEnabled, filterSearchResults, searchMatchCount, pinnedHighlights]);

  // Fix adjacent bold/italic markers that confuse markdown parsers
  const fixMarkdownFormatting = (content: string): string => {
    if (!content) return content;

    // Replace **text***italic* pattern with proper HTML, preserving what comes after
    // Match: **bold***italic*[anything after]
    // This handles cases like **28***a*,**28***b*and
    let fixed = content.replace(/\*\*([^*]+?)\*\*\*([^*]+?)\*([,;:.\s])?/g, (match, bold, italic, after) => {
      // If there's punctuation or space after, preserve it; otherwise add a space before letters
      const suffix = after || '';
      return `<b>${bold}</b><i>${italic}</i>${suffix}`;
    });

    // Handle cases where italic is followed immediately by a letter (like *b*and)
    fixed = fixed.replace(/<\/i>([a-zA-Z])/g, '</i> $1');

    // Also handle *italic***bold* pattern
    fixed = fixed.replace(/\*([^*]+?)\*\*\*([^*]+?)\*\*/g, '<i>$1</i><b>$2</b>');

    return fixed;
  };

  // Convert markdown to HTML (for use before highlighting)
  const markdownToHtml = (content: string): string => {
    if (!content) return content;

    // First fix problematic adjacent markers
    let html = fixMarkdownFormatting(content);

    // Convert remaining bold **text**
    html = html.replace(/\*\*([^*]+?)\*\*/g, '<b>$1</b>');

    // Convert remaining italic *text*
    html = html.replace(/\*([^*]+?)\*/g, '<i>$1</i>');

    return html;
  };

  const handleCopyParagraph = async (paragraphNumber: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedParagraph(paragraphNumber);
      setTimeout(() => setCopiedParagraph(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  // Remove country and kind codes from patent/publication numbers
  const removeCountryAndKindCodes = (str: string): string => {
    if (!str) return str;

    // Remove country code if it starts with two uppercase letters
    if (/^[A-Z]{2}/.test(str)) {
      str = str.substring(2);
    }
    // Remove kind code if it ends with an alphabetic character followed by an optional alphanumeric character
    const kindCodeMatch = /[A-Z][A-Z0-9]?$/.exec(str);
    if (kindCodeMatch) {
      str = str.substring(0, str.length - kindCodeMatch[0].length);
    }

    return str;
  };

  // Handle PDF download for patents and publications
  const handleDownloadPdf = () => {
    if (!document) return;

    const docType = document.file_type || document.type || '';
    if (!['patent', 'publication'].includes(docType.toLowerCase())) return;

    // Get document ID
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-)+/gi, '');
    const numberMatch = docId.match(/\d+/);
    if (numberMatch) {
      docId = numberMatch[0];
    }

    // For publications, remove country and kind codes
    const finalDocId = docType.toLowerCase() === 'publication'
      ? removeCountryAndKindCodes(docId)
      : docId;

    // Open USPTO PDF download URL
    const pdfUrl = `https://image-ppubs.uspto.gov/dirsearch-public/print/downloadPdf/${finalDocId}`;
    window.open(pdfUrl, '_blank');
  };

  const handleDownloadUpload = () => {
    try {
      const resourceId = document?.resource_id || '';
      const fileName = document?.title || 'download';
      const idToken = localStorage.getItem('id_token');

      if (!resourceId || !idToken) {
        useNotificationStore.getState().addNotification({
          type: 'error',
          title: 'Download Failed',
          message: 'Unable to download file. Missing required information.',
        });
        return;
      }

      // Open download URL in new tab - the backend will handle the file download
      const downloadUrl = `${API_CONFIG.baseUrl}/doc/download_upload?resource_id=${encodeURIComponent(resourceId)}&id_token=${encodeURIComponent(idToken)}`;
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Failed to download upload:', error);
      useNotificationStore.getState().addNotification({
        type: 'error',
        title: 'Download Failed',
        message: 'Unable to download the file. Please try again.',
      });
    }
  };

  const getDocumentUrl = () => {
    if (!document) return null;

    // Get document ID and clean it
    let docId = document.resource_id || document.title || '';
    docId = docId.replace(/^(Patent|Publication|Application|Upload)\s+/i, '').trim();
    docId = docId.replace(/^(patent-|publication-|application-)+/gi, '');

    // For patents/publications, ensure US prefix
    const numberMatch = docId.match(/\d+/);
    if (numberMatch) {
      const number = numberMatch[0];
      // If it's a publication (starts with 20), don't add US prefix
      if (number.startsWith('20') && number.length >= 11) {
        return `/US${number}`;
      }
      // For patents (7-8 digits), add US prefix
      return `/US${number}`;
    }

    return null;
  };

  const handleOpenInNewTab = () => {
    const url = getDocumentUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  if (!document) {
    return null;
  }

  return (
    <div className={`${isFullWidth ? 'flex-1' : ''} border-l border-slate-200/80 bg-gradient-to-b from-white/95 via-slate-50/90 to-white/95 shadow-xl shadow-slate-200/70 flex flex-col h-full overflow-hidden`}>
      {/* Header */}
      <div className={`flex-shrink-0 border-b border-slate-200/80 bg-white/80 transition-all ${compactMode ? 'px-4 py-1.5' : 'px-6 py-2.5'}`}>
        
            <div className="">
              <h2
                className={`font-semibold text-gray-900 ${compactMode ? 'text-sm' : 'text-base'} pr-8`}
                title={documentContent?.title || documentName}
              >
                {documentContent?.title || documentName}
              </h2>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(documentContent?.title || documentName);
                  addNotification({
                    type: 'success',
                    title: 'Copied',
                    message: 'Title copied to clipboard',
                    duration: 2000,
                  });
                }}
                className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
                title="Copy title"
              >
                <Copy className="w-3 h-3 text-gray-600" />
              </button>
            </div>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant="outline" className={`bg-blue-50 text-blue-700 border-blue-200 capitalize ${compactMode ? 'text-xs py-0 px-1.5' : 'text-xs'}`}>
                {documentType === 'foreign_patent' ? 'Foreign Patent' : documentType}
              </Badge>
              {document.resource_id && documentType.toLowerCase() !== 'upload' && (
                <Badge variant="outline" className={compactMode ? 'text-xs py-0 px-1.5' : 'text-xs'}>
                  {document.resource_id.replace(/^(patent-|publication-|application-|foreign_patent-)/gi, '')}
                </Badge>
              )}
              {documentContent?.application_number && onViewApplication && (
                <>
                  <span className="text-gray-400">•</span>
                  <button
                    onClick={() => onViewApplication(documentContent.application_number!)}
                    className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    App: {documentContent.application_number}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCompactMode(!compactMode)}
              title={compactMode ? 'Expand header' : 'Compact header'}
              className={compactMode ? 'h-6 w-6 p-0' : 'h-7 w-7 p-0'}
            >
              {compactMode ? <ChevronsDown className="w-3 h-3" /> : <ChevronsUp className="w-3 h-3" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={compactMode ? 'h-6 w-6 p-0' : 'h-7 w-7 p-0'}
                  title="Change document font size"
                >
                  <Type className={compactMode ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                {DOC_FONT_SIZE_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setDocumentFontSize(option.value)}
                    className={`cursor-pointer ${documentFontSize === option.value ? 'bg-blue-50 text-blue-700' : ''}`}
                  >
                    <span style={{ fontSize: option.size }}>
                      {option.label}
                    </span>
                    {documentFontSize === option.value && (
                      <span className="ml-auto text-blue-600">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {!hideFullscreenButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFullscreen(true)}
                title="View fullscreen"
                className={compactMode ? 'h-6 w-6 p-0' : 'h-7 w-7 p-0'}
              >
                <Maximize2 className={compactMode ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
              </Button>
            )}
            {!hideCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className={compactMode ? 'h-6 w-6 p-0' : 'h-7 w-7 p-0'}
              >
                <X className={compactMode ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </Button>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className={`flex items-center gap-2 flex-wrap ${compactMode ? 'mt-1.5' : 'mt-2'}`}>
          {/* Task History Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              setShowTaskHistory(!showTaskHistory);
              if (!showTaskHistory && taskHistory.length === 0) {
                // Fetch task history when opening for the first time
                setTaskHistoryLoading(true);
                try {
                  const history = await taskApi.getTaskHistory();
                  console.log('Fetched task history:', history);

                  // Get document identifier for filtering
                  let docId = document?.resource_id || document?.title || '';
                  docId = docId.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                  docId = docId.replace(/^(patent-|publication-|application-)+/gi, '');

                  // Remove country code and kind code - keep only numbers
                  const cleanDocId = docId.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                  console.log('Filtering for document:', cleanDocId);

                  // Filter history by document ID
                  const filtered = ((history as any).history || []).filter((item: any) => {
                    // Check various ID fields that might match
                    const itemDocId = item.doc_id || '';
                    const itemSelectedNum = item.selected_number || '';
                    const itemAppNum = item.application_number || '';

                    // Clean each field and check for match
                    const cleanItemDocId = itemDocId.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                    const cleanItemSelectedNum = itemSelectedNum.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');
                    const cleanItemAppNum = itemAppNum.replace(/^US/i, '').replace(/[A-Z][0-9]{1,2}$/i, '').replace(/[^0-9]/g, '');

                    return cleanItemDocId === cleanDocId ||
                           cleanItemSelectedNum === cleanDocId ||
                           cleanItemAppNum === cleanDocId;
                  });

                  console.log('Filtered history:', filtered);
                  setTaskHistory(filtered);
                } catch (error) {
                  console.error('Failed to fetch task history:', error);
                } finally {
                  setTaskHistoryLoading(false);
                }
              }
            }}
            title="Task Results"
            className={compactMode ? 'h-6 text-xs px-2' : 'h-7 text-xs px-2'}
          >
            <History className={`${compactMode ? 'w-3 h-3' : 'w-3.5 h-3.5'} ${!isFullWidth ? 'mr-0' : 'mr-1.5'}`} />
            {isFullWidth && <>Task Results</>}
          </Button>

          {/* Toggle Search Controls Button */}
          {!['mpep', 'cfr', 'usc'].includes(documentType.toLowerCase()) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSearchControls(!showSearchControls)}
              className={`${compactMode ? 'h-6 text-xs px-2' : 'h-7 text-xs'} ${showSearchControls ? 'bg-blue-50 border-blue-300' : ''}`}
            >
              <Search className={`${compactMode ? 'w-3 h-3' : 'w-3 h-3'} ${showSearchControls ? 'mr-1' : ''}`} />
              {showSearchControls ? 'Hide Search' : 'Search'}
            </Button>
          )}

          {/* Collapsible Search Controls */}
          {showSearchControls && !['mpep', 'cfr', 'usc'].includes(documentType.toLowerCase()) && (
            <div className={`flex items-center ${compactMode ? 'gap-2' : 'gap-4'}`}>
              {/* Search Query */}
              <div className={`flex items-center gap-2 border border-gray-200 rounded-md bg-white ${compactMode ? 'px-2 py-1' : 'px-3 py-1.5'}`}>
                <Input
                  type="text"
                  placeholder={compactMode ? 'Search...' : 'Search in document...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`border-0 p-0 h-auto focus-visible:ring-0 shadow-none ${compactMode ? 'max-w-[150px] text-xs' : 'max-w-xs'}`}
                />
                {searchMatchCount > 0 && searchQuery.trim() && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-yellow-100 border border-yellow-300">
                      <span className="text-xs font-medium whitespace-nowrap text-yellow-900">
                        {currentSearchIndex + 1} / {searchMatchCount}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 hover:bg-yellow-200 text-yellow-900"
                        onClick={() => {
                          const newIndex = currentSearchIndex - 1 < 0
                            ? searchMatchCount - 1
                            : currentSearchIndex - 1;
                          setCurrentSearchIndex(newIndex);
                          setTimeout(() => {
                            const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);
                            const searchContext = activeTabContent || window.document;
                            const matches = searchContext.querySelectorAll('.search-match');
                            if (matches && matches[newIndex]) {
                              matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 100);
                        }}
                        title="Previous match"
                      >
                        ↑
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 hover:bg-yellow-200 text-yellow-900"
                        onClick={() => {
                          const newIndex = currentSearchIndex + 1 >= searchMatchCount
                            ? 0
                            : currentSearchIndex + 1;
                          setCurrentSearchIndex(newIndex);
                          setTimeout(() => {
                            const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);
                            const searchContext = activeTabContent || window.document;
                            const matches = searchContext.querySelectorAll('.search-match');
                            if (matches && matches[newIndex]) {
                              matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 100);
                        }}
                        title="Next match"
                      >
                        ↓
                      </Button>
                    </div>
                  </>
                )}
                {searchQuery && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={filterSearchResults}
                        onChange={(e) => setFilterSearchResults(e.target.checked)}
                        className="cursor-pointer"
                      />
                      Filter
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-yellow-100"
                      onClick={() => pinCurrentHighlight('search')}
                      disabled={pinnedHighlights.some(p => p.term.toLowerCase() === debouncedSearchQuery.toLowerCase() && p.type === 'search')}
                      title={pinnedHighlights.some(p => p.term.toLowerCase() === debouncedSearchQuery.toLowerCase() && p.type === 'search') ? 'Already pinned' : 'Pin this highlight'}
                    >
                      <Pin className="w-3 h-3" />
                    </Button>
                  </>
                )}
              </div>

              {/* Reference Numeral Search - Only show for US patents/publications */}
              {documentType.toLowerCase() !== 'foreign_patent' && (
                <div className={`flex items-center gap-2 border border-gray-200 rounded-md bg-white ${compactMode ? 'px-2 py-1' : 'px-3 py-1.5'}`}>
                  <Input
                    type="text"
                    placeholder={compactMode ? 'Ref #' : 'Ref numeral (e.g., 100)'}
                    value={referenceNumeral}
                    onChange={(e) => setReferenceNumeral(e.target.value)}
                    className={`border-0 p-0 h-auto focus-visible:ring-0 shadow-none ${compactMode ? 'w-20 text-xs' : 'w-48'}`}
                  />
                  {referenceNumeralMatchCount > 0 && referenceNumeral.trim() && (
                    <>
                      <div className="h-4 w-px bg-gray-300" />
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 border border-blue-300">
                        <span className="text-xs font-medium whitespace-nowrap text-blue-900">
                          {currentReferenceNumeralIndex + 1} / {referenceNumeralMatchCount}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-blue-200 text-blue-900"
                          onClick={() => {
                            const newIndex = currentReferenceNumeralIndex - 1 < 0
                              ? referenceNumeralMatchCount - 1
                              : currentReferenceNumeralIndex - 1;
                            setCurrentReferenceNumeralIndex(newIndex);
                            setTimeout(() => {
                              const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);
                              const searchContext = activeTabContent || window.document;
                              const matches = searchContext.querySelectorAll('.reference-numeral-match');
                              if (matches && matches[newIndex]) {
                                matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          }}
                          title="Previous match"
                        >
                          ↑
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-blue-200 text-blue-900"
                          onClick={() => {
                            const newIndex = currentReferenceNumeralIndex + 1 >= referenceNumeralMatchCount
                              ? 0
                              : currentReferenceNumeralIndex + 1;
                            setCurrentReferenceNumeralIndex(newIndex);
                            setTimeout(() => {
                              const activeTabContent = window.document.querySelector(`[role="tabpanel"][data-state="active"]`);
                              const searchContext = activeTabContent || window.document;
                              const matches = searchContext.querySelectorAll('.reference-numeral-match');
                              if (matches && matches[newIndex]) {
                                matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          }}
                          title="Next match"
                        >
                          ↓
                        </Button>
                      </div>
                    </>
                  )}
                  {referenceNumeral && (
                    <>
                      <div className="h-4 w-px bg-gray-300" />
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={filterReferenceNumerals}
                          onChange={(e) => setFilterReferenceNumerals(e.target.checked)}
                          className="cursor-pointer"
                        />
                        Filter
                      </label>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-blue-100"
                        onClick={() => pinCurrentHighlight('reference')}
                        disabled={pinnedHighlights.some(p => p.term.toLowerCase() === debouncedReferenceNumeral.toLowerCase() && p.type === 'reference')}
                        title={pinnedHighlights.some(p => p.term.toLowerCase() === debouncedReferenceNumeral.toLowerCase() && p.type === 'reference') ? 'Already pinned' : 'Pin this highlight'}
                      >
                        <Pin className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Pinned Highlights Display */}
          {pinnedHighlights.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-gray-500">Pinned:</span>
              {pinnedHighlights.map((pinned, idx) => (
                <div
                  key={`${pinned.type}-${pinned.term}-${idx}`}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded ${pinned.color} text-black text-xs`}
                >
                  <span className="font-medium">{pinned.term}</span>
                  <button
                    onClick={() => unpinHighlight(pinned.term, pinned.type)}
                    className="hover:bg-black/10 rounded p-0.5"
                    title="Remove pinned highlight"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {pinnedHighlights.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1 text-xs text-gray-500 hover:text-gray-700"
                  onClick={clearAllPinnedHighlights}
                  title="Clear all pinned highlights"
                >
                  Clear all
                </Button>
              )}
            </div>
          )}

          {/* Pin buttons for patents/publications */}
          {!['mpep', 'cfr', 'usc'].includes(documentType.toLowerCase()) && documentContent?.claims && (
            <Button
              variant={claimsPinned ? 'default' : 'outline'}
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={() => setClaimsPinned(!claimsPinned)}
              title={claimsPinned ? 'Unpin Claims' : 'Pin Claims'}
            >
              <Pin className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>{claimsPinned ? 'Unpin' : 'Pin'} Claims</>}
            </Button>
          )}

          {['patent', 'publication', 'foreign_patent'].includes(documentType.toLowerCase()) && (
            <Button
              variant={drawingsPinned ? 'default' : 'outline'}
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={() => {
                console.log('Pin Drawings clicked! Current state:', drawingsPinned, 'Document:', document);
                setDrawingsPinned(!drawingsPinned);
              }}
              title={drawingsPinned ? 'Unpin Drawings' : 'Pin Drawings'}
            >
              <ImageIcon className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>{drawingsPinned ? 'Unpin' : 'Pin'} Drawings</>}
            </Button>
          )}

          {['patent', 'publication', 'application', 'upload'].includes(documentType.toLowerCase()) && (
            <Button
              variant={showQuestionHistory ? 'default' : 'outline'}
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={handleOpenQuestionHistory}
              title="Question History"
            >
              <MessageSquare className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>Question History</>}
            </Button>
          )}

          {/* Comments Button */}
          <Button
            variant={showCommentsPanel ? 'default' : 'outline'}
            size="sm"
            className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
            onClick={() => setShowCommentsPanel(!showCommentsPanel)}
            title="Comments"
          >
            <MessageSquarePlus className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
            {isFullWidth && <>Comments</>}
            {comments.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {comments.length}
              </Badge>
            )}
          </Button>

          {getDocumentUrl() && documentType.toLowerCase() !== 'upload' && (
            <Button
              variant="outline"
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={handleOpenInNewTab}
              title="Open in New Tab"
            >
              <ExternalLink className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>Open in New Tab</>}
            </Button>
          )}

          {['patent', 'publication'].includes(documentType.toLowerCase()) && (
            <Button
              variant="outline"
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={handleDownloadPdf}
              title="Download PDF"
            >
              <Download className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>Download PDF</>}
            </Button>
          )}

          {documentType.toLowerCase() === 'upload' && (
            <Button
              variant="outline"
              size="sm"
              className={compactMode ? 'gap-1 h-7 text-xs px-2' : 'gap-2'}
              onClick={handleDownloadUpload}
              title="Download File"
            >
              <Download className={compactMode ? 'w-3 h-3' : 'w-4 h-4'} />
              {isFullWidth && <>Download</>}
            </Button>
          )}
        </div>
      </div>

      {/* Document Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3 animate-pulse" />
            <p className="text-gray-500">Loading document...</p>
          </div>
        </div>
      ) : isOnboarding ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-3 animate-spin" />
            <p className="text-gray-700 font-medium">Onboarding Document</p>
            <p className="text-gray-500 text-sm mt-1">This document is being loaded into your session...</p>
            <p className="text-gray-400 text-xs mt-2">This may take a minute</p>
          </div>
        </div>
      ) : !documentContent ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Failed to load document</p>
            <Button variant="outline" size="sm" onClick={loadDocument} className="mt-4">
              Retry
            </Button>
          </div>
        </div>
      ) : ['mpep', 'cfr', 'usc'].includes(documentType.toLowerCase()) ? (
        // For MPEP, CFR, USC - show content directly without tabs
        <ScrollArea className="flex-1">
          <div className="px-6 py-6">
            {documentContent.content && (
              <div>
                {(documentContent as any).contentType === 'html' ? (
                  <div
                    className="prose max-w-none text-gray-700"
                    style={getFontSizeStyle()}
                    dangerouslySetInnerHTML={{ __html: documentContent.content }}
                  />
                ) : (
                  <div className="prose max-w-none">
                    <pre className="whitespace-pre-wrap text-gray-600" style={getFontSizeStyle()}>
                      {documentContent.content}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      ) : (
        // Patent/Publication layout with optional sidebars
        <div ref={contentRef} className="flex-1 flex flex-row overflow-hidden relative">
          <Tabs value={activeTab} onValueChange={setActiveTab} className={`flex flex-col overflow-hidden ${claimsPinned || drawingsPinned ? 'flex-1' : 'flex-1 min-w-0'}`}>
            <div className="flex-shrink-0 border-b border-gray-200 px-6">
              <TabsList className="bg-transparent h-auto p-0 gap-4">
                <TabsTrigger value="overview" className="border-0 data-[state=active]:bg-transparent data-[state=active]:border-0 data-[state=active]:border-b-[3px] data-[state=active]:border-blue-600 rounded-none px-0 shadow-none">
                  Overview
                </TabsTrigger>
                {claims.length > 0 && !claimsPinned && (
                  <TabsTrigger value="claims" className="border-0 data-[state=active]:bg-transparent data-[state=active]:border-0 data-[state=active]:border-b-[3px] data-[state=active]:border-blue-600 rounded-none px-0 shadow-none">
                    Claims ({claims.length})
                  </TabsTrigger>
                )}
                {documentType?.toLowerCase() === 'upload' ? (
                  <TabsTrigger value="content" className="border-0 data-[state=active]:bg-transparent data-[state=active]:border-0 data-[state=active]:border-b-[3px] data-[state=active]:border-blue-600 rounded-none px-0 shadow-none">
                    Content
                  </TabsTrigger>
                ) : (
                  <TabsTrigger value="specification" className="border-0 data-[state=active]:bg-transparent data-[state=active]:border-0 data-[state=active]:border-b-[3px] data-[state=active]:border-blue-600 rounded-none px-0 shadow-none">
                    Specification
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

          <TabsContent value="overview" className="flex-1 m-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-6 py-6 space-y-6" style={getFontSizeStyle()}>
                {/* Application metadata for patents/publications */}
                {documentContent?.application_metadata && (() => {
                  const am = documentContent.application_metadata!;
                  const isGrant = documentType?.toLowerCase() === 'patent';
                  return (
                    <div className={`rounded-lg border p-4 ${isGrant ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className={`text-xs font-semibold ${isGrant ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-purple-100 text-purple-800 border-purple-300'}`}>
                          {isGrant ? 'US Patent Grant' : 'US Patent Publication'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {am.invention_title && (
                          <div className="col-span-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Title</span>
                            <p className="text-gray-900 font-medium mt-0.5">{am.invention_title}</p>
                          </div>
                        )}
                        {am.application_number && (
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Application No.</span>
                            <p className="text-gray-900 mt-0.5">{am.application_number}</p>
                          </div>
                        )}
                        {am.first_inventor && (
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">First Inventor</span>
                            <p className="text-gray-900 mt-0.5">{am.first_inventor}</p>
                          </div>
                        )}
                        {am.assignee && (
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Assignee</span>
                            <p className="text-gray-900 mt-0.5">{am.assignee}</p>
                          </div>
                        )}
                        {am.filing_date && (
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Filed</span>
                            <p className="text-gray-900 mt-0.5">{am.filing_date}</p>
                          </div>
                        )}
                        {(am.grant_date || am.publication_date) && (
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{isGrant ? 'Issued' : 'Published'}</span>
                            <p className="text-gray-900 mt-0.5">{am.grant_date || am.publication_date}</p>
                          </div>
                        )}
                        {am.all_inventors && am.all_inventors.length > 1 && (
                          <div className="col-span-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">All Inventors</span>
                            <p className="text-gray-900 mt-0.5">{am.all_inventors.join('; ')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {/* AI Summary still being generated: the placeholder above became null,
                    so without this the section would simply be absent and the document
                    would look as though it was never going to have one. */}
                {!summary && String(documentContent?.summary ?? '').trim() === 'unknown' && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">AI Summary</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Being generated…</span>
                    </div>
                  </div>
                )}
                {/* AI Summary */}
                {summary && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">AI Summary</h3>
                    {typeof summary === 'object' && summary !== null ? (
                      <div className="space-y-3">
                        {/* Show output content directly for uploads without section heading */}
                        {documentType?.toLowerCase() === 'upload' && (summary as any).output && (
                          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            {(() => {
                              const outputStr = fixMarkdownFormatting(String((summary as any).output));
                              if (hasAnyHighlighting) {
                                const htmlContent = markdownToHtml(outputStr);
                                const highlighted = applyUnifiedHighlighting(htmlContent, { includeRefNum: true });
                                return (
                                  <div
                                    className="prose max-w-none text-gray-700 leading-relaxed"
                                    style={getFontSizeStyle()}
                                    dangerouslySetInnerHTML={{ __html: highlighted }}
                                  />
                                );
                              }
                              return (
                                <div className="prose max-w-none text-gray-700 leading-relaxed" style={getFontSizeStyle()}>
                                  <Markdown>{outputStr}</Markdown>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        {Object.entries(summary)
                          .filter(([key]) => {
                            // Always filter token metadata keys; also filter 'output' for uploads
                            // (uploads render output content in the block above)
                            const k = key.toLowerCase();
                            if (_TOKEN_KEYS.has(k)) return false;
                            if (documentType?.toLowerCase() === 'upload' && k === 'output') return false;
                            return true;
                          })
                          .map(([key, value]) => {
                          // Determine icon and colors based on key
                          const isProblem = key.toLowerCase().includes('problem');
                          const isSolution = key.toLowerCase().includes('solution');

                          const icon = isProblem ? AlertCircle : isSolution ? Lightbulb : null;
                          const iconColor = isProblem ? 'text-amber-600' : isSolution ? 'text-green-600' : 'text-blue-600';
                          const bgColor = isProblem ? 'bg-amber-50' : isSolution ? 'bg-green-50' : 'bg-blue-50';
                          const borderColor = isProblem ? 'border-amber-200' : isSolution ? 'border-green-200' : 'border-blue-200';

                          const Icon = icon;

                          const isUpload = documentType?.toLowerCase() === 'upload';

                          return (
                            <div key={key} className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
                              <div className="flex items-center gap-2 mb-3">
                                {Icon && <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0`} />}
                                <h4 className="font-semibold text-gray-900">
                                  {key.replace(/_/g, ' ').split(' ').map(word =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                  ).join(' ')}
                                </h4>
                              </div>
                              <div className="text-gray-700 leading-relaxed pl-7" style={getFontSizeStyle()}>
                                {(() => {
                                  const valueStr = String(value);

                                  if (hasAnyHighlighting) {
                                    const htmlContent = isUpload ? markdownToHtml(fixMarkdownFormatting(valueStr)) : valueStr;
                                    const highlighted = applyUnifiedHighlighting(htmlContent, { includeRefNum: true });
                                    return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
                                  }

                                  if (isUpload) {
                                    return (
                                      <div className="prose max-w-none">
                                        <Markdown>{fixMarkdownFormatting(valueStr)}</Markdown>
                                      </div>
                                    );
                                  }

                                  return valueStr;
                                })()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                        {(() => {
                          // If the string is a Python dict with an 'output' key, extract just the output
                          const rawStr = String(summary);
                          const pythonDictMatch = rawStr.startsWith("{") && rawStr.includes("'output'")
                            ? rawStr.match(/'output':\s*'([\s\S]+)',\s*'(?:input_tokens|inputtokens)':/)
                            : null;
                          const extractedStr = pythonDictMatch ? pythonDictMatch[1].replace(/\\'/g, "'") : rawStr;
                          const summaryStr = fixMarkdownFormatting(extractedStr);

                          if (hasAnyHighlighting) {
                            const htmlContent = markdownToHtml(summaryStr);
                            const highlighted = applyUnifiedHighlighting(htmlContent, { includeRefNum: true });
                            return (
                              <div
                                className="prose max-w-none text-gray-700"
                                style={getFontSizeStyle()}
                                dangerouslySetInnerHTML={{ __html: highlighted }}
                              />
                            );
                          }

                          return (
                            <div className="prose max-w-none text-gray-700 [&>p]:inline" style={getFontSizeStyle()}>
                              <Markdown>{summaryStr}</Markdown>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Abstract */}
                {documentContent.abstract && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Abstract</h3>
                    {(() => {
                      const abstractText = fixMarkdownFormatting(documentContent.abstract);

                      if (hasAnyHighlighting) {
                        // Convert markdown to HTML first, then apply highlighting
                        const htmlContent = markdownToHtml(abstractText);
                        const highlighted = applyUnifiedHighlighting(htmlContent, { includeRefNum: true });
                        return (
                          <div
                            className="prose max-w-none text-gray-600 leading-relaxed"
                            style={getFontSizeStyle()}
                            dangerouslySetInnerHTML={{ __html: highlighted }}
                          />
                        );
                      }

                      return (
                        <div className="prose max-w-none text-gray-600 leading-relaxed [&>p]:inline" style={getFontSizeStyle()}>
                          <Markdown>{abstractText}</Markdown>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Document Content (MPEP, CFR, USC) - Uploads moved to separate Content tab */}
                {documentContent.content && documentType?.toLowerCase() !== 'upload' && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Content</h3>
                    {(() => {
                      const isHtml = (documentContent as any).contentType === 'html';
                      const content = documentContent.content;

                      if (hasAnyHighlighting) {
                        const highlighted = applyUnifiedHighlighting(content, { includeRefNum: true });
                        return (
                          <div
                            className="prose max-w-none text-gray-700"
                            style={getFontSizeStyle()}
                            dangerouslySetInnerHTML={{ __html: highlighted }}
                          />
                        );
                      }

                      if (isHtml) {
                        return (
                          <div
                            className="prose max-w-none text-gray-700"
                            style={getFontSizeStyle()}
                            dangerouslySetInnerHTML={{ __html: content }}
                          />
                        );
                      }

                      return (
                        <div className="prose max-w-none" style={getFontSizeStyle()}>
                          <pre className="whitespace-pre-wrap text-gray-600" style={getFontSizeStyle()}>
                            {content}
                          </pre>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Key Stats */}
                {(claims.length > 0 || sections.length > 0) && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Document Statistics</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {claims.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-2xl font-semibold text-gray-900">{claims.length}</div>
                          <div className="text-sm text-gray-600">Claims</div>
                        </div>
                      )}
                      {sections.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-2xl font-semibold text-gray-900">
                            {sections.reduce((total, section) => total + section.paragraphs.length, 0)}
                          </div>
                          <div className="text-sm text-gray-600">Paragraphs</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {claims.length > 0 && (
            <TabsContent value="claims" className="flex-1 m-0 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="px-6 py-6 space-y-4" style={getFontSizeStyle()}>
                  {claims.map((claim, index) => {
                    const claimText = claim.text || '';

                    // Apply highlighting to claim text
                    const query = debouncedSearchQuery.trim();
                    const claimsFilter = debouncedClaimsFilterText.trim();
                    const refNum = debouncedReferenceNumeral.trim();
                    let highlightedClaimText = claimText;
                    const hasHighlighting = query || claimsFilter || refNum || pinnedHighlights.length > 0;

                    if (query || claimsFilter || refNum) {
                      // First convert markdown to HTML
                      highlightedClaimText = markdownToHtml(highlightedClaimText);

                      // Apply multiple highlights simultaneously to avoid conflicts
                      if ((query && claimsFilter) || (query && refNum) || (claimsFilter && refNum) || (query && claimsFilter && refNum)) {
                        // Multiple filters active - need to handle overlaps
                        const matches: Array<{ start: number; end: number; type: 'search' | 'claims' | 'refnum'; text: string }> = [];
                        const lowerContent = highlightedClaimText.toLowerCase();

                        // Find all query matches
                        if (query) {
                          const lowerQuery = query.toLowerCase();
                          let pos = 0;
                          while ((pos = lowerContent.indexOf(lowerQuery, pos)) !== -1) {
                            matches.push({
                              start: pos,
                              end: pos + query.length,
                              type: 'search',
                              text: highlightedClaimText.substring(pos, pos + query.length)
                            });
                            pos++;
                          }
                        }

                        // Find all claims filter matches
                        if (claimsFilter) {
                          const lowerClaimsFilter = claimsFilter.toLowerCase();
                          let pos = 0;
                          while ((pos = lowerContent.indexOf(lowerClaimsFilter, pos)) !== -1) {
                            matches.push({
                              start: pos,
                              end: pos + claimsFilter.length,
                              type: 'claims',
                              text: highlightedClaimText.substring(pos, pos + claimsFilter.length)
                            });
                            pos++;
                          }
                        }

                        // Find all reference numeral matches (only in bold)
                        if (refNum) {
                          const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                          let match;
                          while ((match = refNumRegex.exec(highlightedClaimText)) !== null) {
                            matches.push({
                              start: match.index,
                              end: match.index + match[0].length,
                              type: 'refnum',
                              text: match[0]
                            });
                          }
                        }

                        // Sort and remove overlaps
                        matches.sort((a, b) => {
                          if (a.start !== b.start) return a.start - b.start;
                          return (b.end - b.start) - (a.end - a.start);
                        });

                        const usedRanges: typeof matches = [];
                        matches.forEach(match => {
                          const overlaps = usedRanges.some(range =>
                            (match.start >= range.start && match.start < range.end) ||
                            (match.end > range.start && match.end <= range.end) ||
                            (match.start <= range.start && match.end >= range.end)
                          );

                          if (!overlaps) {
                            usedRanges.push(match);
                          }
                        });

                        usedRanges.sort((a, b) => a.start - b.start);

                        // Build highlighted text
                        let result = '';
                        let lastEnd = 0;

                        usedRanges.forEach(match => {
                          if (match.start > lastEnd) {
                            result += highlightedClaimText.substring(lastEnd, match.start);
                          }

                          if (match.type === 'search') {
                            result += `<mark class="bg-yellow-300 text-black search-match">${match.text}</mark>`;
                          } else if (match.type === 'claims') {
                            result += `<mark class="bg-green-300 text-black claims-filter-match">${match.text}</mark>`;
                          } else if (match.type === 'refnum') {
                            result += `<mark class="bg-blue-300 text-black reference-numeral-match">${match.text}</mark>`;
                          }

                          lastEnd = match.end;
                        });

                        if (lastEnd < highlightedClaimText.length) {
                          result += highlightedClaimText.substring(lastEnd);
                        }

                        highlightedClaimText = result;
                      } else if (query) {
                        // Only search query
                        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, '<mark class="bg-yellow-300 text-black search-match">$1</mark>');
                      } else if (claimsFilter) {
                        // Only claims filter - always highlight if text is selected
                        const regex = new RegExp(`(${claimsFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, '<mark class="bg-green-300 text-black claims-filter-match">$1</mark>');
                      } else if (refNum) {
                        // Only reference numeral (must be in bold)
                        const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(refNumRegex, '<mark class="bg-blue-300 text-black reference-numeral-match"><b>$1</b></mark>');
                      }
                    }

                    // Apply pinned highlights to claims
                    if (pinnedHighlights.length > 0) {
                      // Convert markdown if not already converted
                      if (!query && !claimsFilter && !refNum) {
                        highlightedClaimText = markdownToHtml(highlightedClaimText);
                      }
                      pinnedHighlights.forEach(pinned => {
                        const escapedTerm = pinned.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                        const regex = new RegExp(`(${escapedTerm})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, `<mark class="pinned-highlight" style="background-color: ${pinned.cssColor}; color: black;">$1</mark>`);
                      });
                    }

                    return (
                      <div key={index} id={`claim-${claim.claim_number || index + 1}`} className="border border-gray-200 rounded-lg p-4">
                        <div className="font-medium text-gray-900 mb-2">
                          Claim {claim.claim_number || index + 1}
                        </div>
                        <div className="prose max-w-none text-gray-600 leading-relaxed" style={getFontSizeStyle()}>
                          {hasHighlighting ? (
                            <div dangerouslySetInnerHTML={{ __html: highlightedClaimText }} />
                          ) : (
                            <div className="[&>p]:inline">
                              <Markdown>{fixMarkdownFormatting(claimText)}</Markdown>
                            </div>
                          )}
                        </div>
                        {claim.dependencies && claim.dependencies.length > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            Depends on: {claim.dependencies.join(', ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>
          )}

          {/* Content tab for uploads */}
          <TabsContent value="content" className="flex-1 m-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-6 py-6">
                {documentContent.content && (
                  <div className="prose max-w-none text-gray-700" style={getFontSizeStyle()}>
                    <pre className="whitespace-pre-wrap text-gray-600" style={getFontSizeStyle()}>
                      {documentContent.content}
                    </pre>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="specification" className="flex-1 m-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-6 py-6 space-y-6" ref={descriptionRef}>
                {processedDescription ? (
                  <div
                    className="prose max-w-none text-gray-700 patent-description"
                    dangerouslySetInnerHTML={{ __html: processedDescription }}
                    style={{
                      ...getFontSizeStyle(),
                      lineHeight: '1.75'
                    }}
                  />
                ) : sections.length > 0 ? (
                  sections.map((section, sectionIdx) => (
                    <div key={sectionIdx} className="space-y-4">
                      {/* Section Title */}
                      {section.title && (
                        <h3 className="text-base font-semibold text-gray-900 border-b border-gray-300 pb-2">
                          {section.title}
                        </h3>
                      )}

                      {/* Section Paragraphs */}
                      <div className="space-y-3">
                        {section.paragraphs.map((para) => {
                          // Skip if no content
                          if (!para.content) return null;

                          // Apply highlighting to paragraph content
                          let highlightedContent = para.content;
                          const query = debouncedSearchQuery.trim();
                          const claimsFilter = debouncedClaimsFilterText.trim();
                          const refNum = debouncedReferenceNumeral.trim();
                          const hasPinnedHighlights = pinnedHighlights.length > 0;

                          if (query || claimsFilter || refNum || hasPinnedHighlights) {
                            // First convert markdown to HTML
                            highlightedContent = markdownToHtml(highlightedContent);

                            // Unified highlighting - collect ALL matches first, then apply in one pass
                            const matches: Array<{ start: number; end: number; type: string; text: string; cssColor?: string }> = [];
                            const lowerContent = highlightedContent.toLowerCase();

                            // Find all query matches
                            if (query) {
                              const lowerQuery = query.toLowerCase();
                              let pos = 0;
                              while ((pos = lowerContent.indexOf(lowerQuery, pos)) !== -1) {
                                matches.push({
                                  start: pos,
                                  end: pos + query.length,
                                  type: 'search',
                                  text: highlightedContent.substring(pos, pos + query.length)
                                });
                                pos++;
                              }
                            }

                            // Find all claims filter matches
                            if (claimsFilter) {
                              const lowerClaimsFilter = claimsFilter.toLowerCase();
                              let pos = 0;
                              while ((pos = lowerContent.indexOf(lowerClaimsFilter, pos)) !== -1) {
                                matches.push({
                                  start: pos,
                                  end: pos + claimsFilter.length,
                                  type: 'claims',
                                  text: highlightedContent.substring(pos, pos + claimsFilter.length)
                                });
                                pos++;
                              }
                            }

                            // Find all reference numeral matches (only in bold)
                            if (refNum) {
                              const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                              let match;
                              while ((match = refNumRegex.exec(highlightedContent)) !== null) {
                                matches.push({
                                  start: match.index,
                                  end: match.index + match[0].length,
                                  type: 'refnum',
                                  text: match[0]
                                });
                              }
                            }

                            // Find all pinned highlight matches
                            if (hasPinnedHighlights) {
                              pinnedHighlights.forEach(pinned => {
                                const lowerTerm = pinned.term.toLowerCase();
                                let pos = 0;
                                while ((pos = lowerContent.indexOf(lowerTerm, pos)) !== -1) {
                                  matches.push({
                                    start: pos,
                                    end: pos + pinned.term.length,
                                    type: 'pinned',
                                    text: highlightedContent.substring(pos, pos + pinned.term.length),
                                    cssColor: pinned.cssColor
                                  });
                                  pos++;
                                }
                              });
                            }

                            if (matches.length > 0) {
                              // Sort by length (longer first), then by start position
                              matches.sort((a, b) => {
                                const lenDiff = (b.end - b.start) - (a.end - a.start);
                                if (lenDiff !== 0) return lenDiff;
                                return a.start - b.start;
                              });

                              // Build non-overlapping ranges (longer matches take precedence)
                              const usedRanges: typeof matches = [];
                              matches.forEach(match => {
                                const overlaps = usedRanges.some(range =>
                                  (match.start >= range.start && match.start < range.end) ||
                                  (match.end > range.start && match.end <= range.end) ||
                                  (match.start <= range.start && match.end >= range.end)
                                );

                                if (!overlaps) {
                                  usedRanges.push(match);
                                }
                              });

                              // Sort by position for building final string
                              usedRanges.sort((a, b) => a.start - b.start);

                              // Build highlighted text
                              let result = '';
                              let lastEnd = 0;

                              usedRanges.forEach(match => {
                                if (match.start > lastEnd) {
                                  result += highlightedContent.substring(lastEnd, match.start);
                                }

                                if (match.type === 'search') {
                                  result += `<mark style="background-color: #fde047; color: black;" class="search-match">${match.text}</mark>`;
                                } else if (match.type === 'claims') {
                                  result += `<mark style="background-color: #86efac; color: black;" class="claims-filter-match">${match.text}</mark>`;
                                } else if (match.type === 'refnum') {
                                  result += `<mark style="background-color: #93c5fd; color: black;" class="reference-numeral-match">${match.text}</mark>`;
                                } else if (match.type === 'pinned') {
                                  result += `<mark style="background-color: ${match.cssColor}; color: black;" class="pinned-highlight">${match.text}</mark>`;
                                }

                                lastEnd = match.end;
                              });

                              if (lastEnd < highlightedContent.length) {
                                result += highlightedContent.substring(lastEnd);
                              }

                              highlightedContent = result;
                            }
                          }

                          // Check if paragraph should be shown when filtering
                          const hasSearchMatch = query && para.content.toLowerCase().includes(query.toLowerCase());
                          const hasClaimsMatch = claimsFilter && claimsFilterEnabled && para.content.toLowerCase().includes(claimsFilter.toLowerCase());
                          const hasRefNumMatch = refNum && new RegExp(`\\*\\*${refNum}[a-zA-Z]?\\*\\*`, 'i').test(para.content);

                          // Show paragraph if:
                          // - No filters are enabled, OR
                          // - Has a match for any enabled filter
                          const shouldShow = (!filterSearchResults && !claimsFilterEnabled && !filterReferenceNumerals)
                            || (filterSearchResults && hasSearchMatch)
                            || (claimsFilterEnabled && hasClaimsMatch)
                            || (filterReferenceNumerals && hasRefNumMatch);

                          if (!shouldShow) return null;

                          // Check if paragraph number is 0000 or 0 (hide if so)
                          const isZeroParagraph = para.number === '0000' || para.number === '0' || para.number === 0 || parseInt(para.number) === 0;

                          return (
                            <div
                              key={`${sectionIdx}-${para.number}`}
                              {...(!isZeroParagraph && { id: `p-${para.number}` })}
                              className="group flex items-start gap-4"
                            >
                              {!isZeroParagraph && (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-sm font-mono font-semibold text-gray-600 mt-1">
                                    [{para.number}]
                                  </span>
                                  <button
                                    onClick={() => handleCopyParagraph(para.number, para.content || '')}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
                                    title="Copy paragraph"
                                  >
                                    {copiedParagraph === para.number ? (
                                      <Check className="w-3.5 h-3.5 text-green-600" />
                                    ) : (
                                      <Copy className="w-3.5 h-3.5 text-gray-500" />
                                    )}
                                  </button>
                                </div>
                              )}
                              <div className="text-gray-700 leading-relaxed flex-1" style={getFontSizeStyle()}>
                                {query || claimsFilter || refNum || hasPinnedHighlights ? (
                                  <div className="prose max-w-none" style={getFontSizeStyle()} dangerouslySetInnerHTML={{ __html: highlightedContent }} />
                                ) : (
                                  <div className="prose max-w-none [&>p]:inline" style={getFontSizeStyle()}>
                                    <Markdown>{fixMarkdownFormatting(para.content)}</Markdown>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No specification content available</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Pinned Drawings Sidebar */}
        {drawingsPinned && ['patent', 'publication', 'foreign_patent'].includes(documentType.toLowerCase()) && (
          <div
            ref={drawingViewerRef}
            className="flex-shrink-0 min-w-0 relative border-l border-gray-200"
            style={{
              width: selectedDrawingIndex !== null
                ? `${Math.max(drawingViewerWidth, window.innerWidth * 0.4)}px`
                : `${drawingViewerWidth}px`,
              maxWidth: '60vw'
            }}
          >
            {/* Resize handle */}
            <div
              className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10 hover:bg-blue-500 ${isResizingDrawingWidth ? 'bg-blue-500' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsResizingDrawingWidth(true);
              }}
              title="Drag to resize width"
            />

            <ScrollArea className="h-full">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {epoDrawings.length > 0 ? (
                      epoSelectedDrawing !== null
                        ? `${epoDrawings[epoSelectedDrawing]?.description} — p.${epoCurrentPage}/${epoDrawings[epoSelectedDrawing]?.num_pages}`
                        : `Drawings (${epoDrawings.reduce((s, d) => s + d.num_pages, 0)} pages)`
                    ) : selectedDrawingIndex !== null ? (
                      documentType.toLowerCase() === 'foreign_patent'
                        ? <>Sheet {selectedDrawingIndex + 1}</>
                        : selectedDrawingIndex === 0 ? <>Cover Sheet</> : <>Sheet {selectedDrawingIndex}</>
                    ) : (
                      `Drawings${drawingsUrls.length > 0 ? ` (${drawingsUrls.length})` : ''}`
                    )}
                  </h3>
                  {(epoSelectedDrawing !== null || selectedDrawingIndex !== null) && (
                    <Button
                      onClick={() => {
                        if (epoSelectedDrawing !== null) {
                          setEpoSelectedDrawing(null);
                          setEpoCurrentPage(1);
                          if (epoDrawingBlobUrl) { URL.revokeObjectURL(epoDrawingBlobUrl); setEpoDrawingBlobUrl(null); }
                        } else {
                          setSelectedDrawingIndex(null);
                          setDrawingRotation(0);
                          setDrawingZoom(100);
                        }
                      }}
                      variant="outline"
                      size="sm"
                    >
                      ← All
                    </Button>
                  )}
                </div>

                {/* Zoom and Rotation controls for full view */}
                {selectedDrawingIndex !== null && (
                  <div className="flex items-center gap-3 mb-4 justify-center">
                    <div className="flex items-center gap-1">
                      <Button
                        onClick={() => setDrawingZoom((prev) => Math.max(prev - 25, 25))}
                        variant="outline"
                        size="sm"
                        title="Zoom Out"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setDrawingZoom(100)}
                        variant="outline"
                        size="sm"
                        className="min-w-[60px]"
                        title="Reset Zoom"
                      >
                        {drawingZoom}%
                      </Button>
                      <Button
                        onClick={() => setDrawingZoom((prev) => Math.min(prev + 25, 200))}
                        variant="outline"
                        size="sm"
                        title="Zoom In"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </Button>
                    </div>

                    <Separator orientation="vertical" className="h-6" />

                    <div className="flex items-center gap-1">
                      <Button
                        onClick={() => setDrawingRotation((prev) => (prev - 90 + 360) % 360)}
                        variant="outline"
                        size="sm"
                        title="Rotate Left 90°"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setDrawingRotation((prev) => (prev + 90) % 360)}
                        variant="outline"
                        size="sm"
                        title="Rotate Right 90°"
                      >
                        <RotateCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Drawing display */}
                {loadingDrawings ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <p className="text-gray-500 text-sm">Loading drawings...</p>
                  </div>
                ) : epoDrawings.length > 0 ? (
                  // EPO drawings (foreign patents) — PDF viewer with page navigation
                  epoSelectedDrawing !== null ? (
                    <div className="flex flex-col gap-3">
                      {/* Page navigation */}
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          onClick={() => { const p = epoCurrentPage - 1; setEpoCurrentPage(p); loadEpoPage(epoSelectedDrawing, p); }}
                          disabled={epoCurrentPage <= 1 || epoLoadingPage}
                          variant="outline" size="sm"
                        >←</Button>
                        <span className="text-sm text-gray-600">Page {epoCurrentPage} / {epoDrawings[epoSelectedDrawing]?.num_pages}</span>
                        <Button
                          onClick={() => { const p = epoCurrentPage + 1; setEpoCurrentPage(p); loadEpoPage(epoSelectedDrawing, p); }}
                          disabled={epoCurrentPage >= (epoDrawings[epoSelectedDrawing]?.num_pages ?? 1) || epoLoadingPage}
                          variant="outline" size="sm"
                        >→</Button>
                      </div>
                      {/* PDF viewer */}
                      {epoLoadingPage ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : epoDrawingBlobUrl ? (
                        <iframe
                          src={epoDrawingBlobUrl}
                          className="w-full border border-gray-200 rounded"
                          style={{ height: '70vh' }}
                          title={`Drawing page ${epoCurrentPage}`}
                        />
                      ) : (
                        <div className="text-center text-gray-500 text-sm py-8">Failed to load page</div>
                      )}
                    </div>
                  ) : (
                    // Drawing section list
                    <div className="flex flex-col gap-2">
                      {epoDrawings.map((drawing, idx) => (
                        <button
                          key={idx}
                          onClick={() => { setEpoSelectedDrawing(idx); setEpoCurrentPage(1); loadEpoPage(idx, 1); }}
                          className="border border-gray-200 rounded-lg p-3 hover:border-blue-500 hover:shadow-md transition-all text-left"
                        >
                          <p className="text-sm font-medium text-gray-800">{drawing.description}</p>
                          <p className="text-xs text-gray-500 mt-1">{drawing.num_pages} page{drawing.num_pages !== 1 ? 's' : ''}</p>
                        </button>
                      ))}
                    </div>
                  )
                ) : drawingsUrls.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <ImageIcon className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-gray-500 text-sm">No drawings available</p>
                    <p className="text-gray-400 text-xs mt-1">Drawings may not be available for this document</p>
                  </div>
                ) : selectedDrawingIndex !== null ? (
                  <div className="flex justify-center items-center">
                    {drawingsUrls[selectedDrawingIndex]?.toLowerCase().includes('.pdf') ? (
                      <iframe
                        src={drawingsUrls[selectedDrawingIndex]}
                        className="w-full border-0 rounded"
                        style={{ height: '70vh' }}
                        title={documentType.toLowerCase() === 'foreign_patent' ? `Sheet ${selectedDrawingIndex + 1}` : selectedDrawingIndex === 0 ? 'Cover Sheet' : `Sheet ${selectedDrawingIndex}`}
                      />
                    ) : (
                      <img
                        src={drawingsUrls[selectedDrawingIndex]}
                        alt={documentType.toLowerCase() === 'foreign_patent' ? `Sheet ${selectedDrawingIndex + 1}` : selectedDrawingIndex === 0 ? 'Cover Sheet' : `Sheet ${selectedDrawingIndex}`}
                        className="max-w-full h-auto"
                        style={{
                          transform: `rotate(${drawingRotation}deg) scale(${drawingZoom / 100})`,
                          transformOrigin: 'center'
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {drawingsUrls.map((drawing: string, idx: number) => {
                      const isForeignPatent = documentType.toLowerCase() === 'foreign_patent';
                      const isPdf = drawing.toLowerCase().includes('.pdf');
                      const label = isForeignPatent ? `Sheet ${idx + 1}` : idx === 0 ? 'Cover Sheet' : `Sheet ${idx}`;
                      return (
                        <button
                          key={idx}
                          onClick={() => setSelectedDrawingIndex(idx)}
                          className="border border-gray-200 rounded-lg p-2 hover:border-blue-500 hover:shadow-md transition-all"
                        >
                          {isPdf ? (
                            <div className="w-full overflow-hidden rounded bg-white" style={{ aspectRatio: '1 / 1.414' }}>
                              <iframe
                                src={`${drawing}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                                className="w-full h-full border-0"
                                style={{ pointerEvents: 'none' }}
                                title={label}
                              />
                            </div>
                          ) : (
                            <img
                              src={drawing}
                              alt={label}
                              className="w-full h-auto"
                            />
                          )}
                          <p className="text-xs text-gray-600 mt-2">{label}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Pinned Claims Sidebar */}
        {claimsPinned && documentContent?.claims && (
          <div className="w-80 flex-shrink-0 min-w-0 border-l border-gray-200" ref={claimsSidebarRef}>
            <ScrollArea className="h-full">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Claims</h3>

                <div className="space-y-2 mb-4">
                  <p className="text-xs text-gray-600">
                    Select text from claims to highlight in specification
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-100 flex-1">
                        <input
                          type="checkbox"
                          checked={claimsFilterEnabled}
                          onChange={(e) => setClaimsFilterEnabled(e.target.checked)}
                          className="cursor-pointer w-4 h-4"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            Filter by selected text
                          </div>
                          {claimsFilterText && (
                            <div className="text-xs mt-1 text-gray-600">
                              "{claimsFilterText.length > 30 ? claimsFilterText.substring(0, 30) + '...' : claimsFilterText}"
                            </div>
                          )}
                        </div>
                      </label>
                      {claimsFilterText && (
                        <Button
                          onClick={() => {
                            setClaimsFilterText('');
                            setClaimsFilterEnabled(false);
                          }}
                          variant="destructive"
                          size="sm"
                        >
                          Clear
                        </Button>
                      )}
                    </div>

                    {/* Navigation controls for claims filter */}
                    {claimsFilterMatchCount > 0 && claimsFilterText.trim() && (
                      <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-green-100 border border-green-300">
                        <span className="text-xs font-medium whitespace-nowrap text-green-900">
                          {currentClaimsFilterIndex + 1} / {claimsFilterMatchCount}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-green-200 text-green-900"
                          onClick={() => {
                            const newIndex = currentClaimsFilterIndex - 1 < 0
                              ? claimsFilterMatchCount - 1
                              : currentClaimsFilterIndex - 1;
                            setCurrentClaimsFilterIndex(newIndex);
                            setTimeout(() => {
                              const matches = window.document.querySelectorAll('.claims-filter-match');
                              if (matches && matches[newIndex]) {
                                matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          }}
                          title="Previous match"
                        >
                          ↑
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-green-200 text-green-900"
                          onClick={() => {
                            const newIndex = currentClaimsFilterIndex + 1 >= claimsFilterMatchCount
                              ? 0
                              : currentClaimsFilterIndex + 1;
                            setCurrentClaimsFilterIndex(newIndex);
                            setTimeout(() => {
                              const matches = window.document.querySelectorAll('.claims-filter-match');
                              if (matches && matches[newIndex]) {
                                matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          }}
                          title="Next match"
                        >
                          ↓
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-4">
                  {claims.map((claim, idx) => {
                    const claimText = claim.text || '';
                    const isDependentClaim = /(?:according\s+to|of)\s+(?:any\s+of\s+)?claims?\s+\d+/i.test(claimText);

                    // Apply highlighting to claim text
                    const query = debouncedSearchQuery.trim();
                    const claimsFilter = debouncedClaimsFilterText.trim();
                    const refNum = debouncedReferenceNumeral.trim();
                    let highlightedClaimText = claimText;
                    const hasHighlighting = query || claimsFilter || refNum || pinnedHighlights.length > 0;

                    if (query || claimsFilter || refNum) {
                      // First convert markdown to HTML
                      highlightedClaimText = markdownToHtml(highlightedClaimText);

                      // Apply multiple highlights simultaneously to avoid conflicts
                      if ((query && claimsFilter) || (query && refNum) || (claimsFilter && refNum) || (query && claimsFilter && refNum)) {
                        // Multiple filters active - need to handle overlaps
                        const matches: Array<{ start: number; end: number; type: 'search' | 'claims' | 'refnum'; text: string }> = [];
                        const lowerContent = highlightedClaimText.toLowerCase();

                        // Find all query matches
                        if (query) {
                          const lowerQuery = query.toLowerCase();
                          let pos = 0;
                          while ((pos = lowerContent.indexOf(lowerQuery, pos)) !== -1) {
                            matches.push({
                              start: pos,
                              end: pos + query.length,
                              type: 'search',
                              text: highlightedClaimText.substring(pos, pos + query.length)
                            });
                            pos++;
                          }
                        }

                        // Find all claims filter matches
                        if (claimsFilter) {
                          const lowerClaimsFilter = claimsFilter.toLowerCase();
                          let pos = 0;
                          while ((pos = lowerContent.indexOf(lowerClaimsFilter, pos)) !== -1) {
                            matches.push({
                              start: pos,
                              end: pos + claimsFilter.length,
                              type: 'claims',
                              text: highlightedClaimText.substring(pos, pos + claimsFilter.length)
                            });
                            pos++;
                          }
                        }

                        // Find all reference numeral matches (only in bold)
                        if (refNum) {
                          const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                          let match;
                          while ((match = refNumRegex.exec(highlightedClaimText)) !== null) {
                            matches.push({
                              start: match.index,
                              end: match.index + match[0].length,
                              type: 'refnum',
                              text: match[0]
                            });
                          }
                        }

                        // Sort and remove overlaps
                        matches.sort((a, b) => {
                          if (a.start !== b.start) return a.start - b.start;
                          return (b.end - b.start) - (a.end - a.start);
                        });

                        const usedRanges: typeof matches = [];
                        matches.forEach(match => {
                          const overlaps = usedRanges.some(range =>
                            (match.start >= range.start && match.start < range.end) ||
                            (match.end > range.start && match.end <= range.end) ||
                            (match.start <= range.start && match.end >= range.end)
                          );

                          if (!overlaps) {
                            usedRanges.push(match);
                          }
                        });

                        usedRanges.sort((a, b) => a.start - b.start);

                        // Build highlighted text
                        let result = '';
                        let lastEnd = 0;

                        usedRanges.forEach(match => {
                          if (match.start > lastEnd) {
                            result += highlightedClaimText.substring(lastEnd, match.start);
                          }

                          if (match.type === 'search') {
                            result += `<mark class="bg-yellow-300 text-black search-match">${match.text}</mark>`;
                          } else if (match.type === 'claims') {
                            result += `<mark class="bg-green-300 text-black claims-filter-match">${match.text}</mark>`;
                          } else if (match.type === 'refnum') {
                            result += `<mark class="bg-blue-300 text-black reference-numeral-match">${match.text}</mark>`;
                          }

                          lastEnd = match.end;
                        });

                        if (lastEnd < highlightedClaimText.length) {
                          result += highlightedClaimText.substring(lastEnd);
                        }

                        highlightedClaimText = result;
                      } else if (query) {
                        // Only search query
                        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, '<mark class="bg-yellow-300 text-black search-match">$1</mark>');
                      } else if (claimsFilter) {
                        // Only claims filter - always highlight if text is selected
                        const regex = new RegExp(`(${claimsFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, '<mark class="bg-green-300 text-black claims-filter-match">$1</mark>');
                      } else if (refNum) {
                        // Only reference numeral (must be in bold)
                        const refNumRegex = new RegExp(`<b>(${refNum}[a-zA-Z]?)</b>`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(refNumRegex, '<mark class="bg-blue-300 text-black reference-numeral-match"><b>$1</b></mark>');
                      }
                    }

                    // Apply pinned highlights to sidebar claims
                    if (pinnedHighlights.length > 0) {
                      // Convert markdown if not already converted
                      if (!query && !claimsFilter && !refNum) {
                        highlightedClaimText = markdownToHtml(highlightedClaimText);
                      }
                      pinnedHighlights.forEach(pinned => {
                        const escapedTerm = pinned.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                        const regex = new RegExp(`(${escapedTerm})`, 'gi');
                        highlightedClaimText = highlightedClaimText.replace(regex, `<mark class="pinned-highlight" style="background-color: ${pinned.cssColor}; color: black;">$1</mark>`);
                      });
                    }

                    return (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border-l-4 ${
                          isDependentClaim
                            ? 'border-blue-400 bg-blue-50/50'
                            : 'border-green-500 bg-green-50'
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                            isDependentClaim
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {claim.claim_number || idx + 1}
                          </div>
                          <div className="flex-1 select-text cursor-text">
                            <div className="prose max-w-none leading-relaxed text-gray-700" style={getFontSizeStyle()}>
                              {hasHighlighting ? (
                                <div dangerouslySetInnerHTML={{ __html: highlightedClaimText }} />
                              ) : (
                                <div className="[&>p]:inline">
                                  <Markdown>{fixMarkdownFormatting(claimText)}</Markdown>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Task History Panel */}
        {showTaskHistory && (
          <div
            className="flex-shrink-0 min-w-0 relative border-l border-gray-200 flex flex-col bg-white"
            style={{
              width: `${taskHistoryWidth}px`,
              minWidth: '300px',
              maxWidth: '50vw'
            }}
            ref={taskHistoryContainerRef}
          >
            {/* Resize handle */}
            <div
              className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10 hover:bg-purple-500 ${isResizingTaskHistory ? 'bg-purple-500' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsResizingTaskHistory(true);
              }}
              title="Drag to resize width"
            />

            {/* Header */}
            <div className="flex-shrink-0 border-b border-gray-200 p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {selectedTaskResult && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedTaskResult(null);
                        setShowTaskResultViewer(false);
                      }}
                      className="h-8 w-8"
                      title="Back to list"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedTaskResult ? selectedTaskResult.taskName : 'Task Results'}
                  </h3>
                </div>
                <div className="flex items-center gap-1">
                  {selectedTaskResult && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowTaskResultFullscreen(true)}
                      className="h-8 w-8"
                      title="View fullscreen"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setShowTaskHistory(false);
                      setSelectedTaskResult(null);
                      setShowTaskResultViewer(false);
                    }}
                    className="h-8 w-8"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {!selectedTaskResult && (
                <p className="text-sm text-gray-600">
                  {taskHistory.length} task{taskHistory.length !== 1 ? 's' : ''} found for this document
                </p>
              )}
              {selectedTaskResult && (
                <p className="text-sm text-gray-600">
                  {(() => {
                    try {
                      const ts = typeof selectedTaskResult.timestamp === 'number'
                        ? (selectedTaskResult.timestamp < 10000000000 ? selectedTaskResult.timestamp * 1000 : selectedTaskResult.timestamp)
                        : selectedTaskResult.timestamp;
                      const date = new Date(ts as any);
                      if (!isNaN(date.getTime())) {
                        return date.toLocaleString();
                      }
                      if (typeof ts === 'string' && ts.trim()) {
                        return ts;
                      }
                      return 'Unknown date';
                    } catch (e) {
                      if (typeof selectedTaskResult.timestamp === 'string' && selectedTaskResult.timestamp.trim()) {
                        return selectedTaskResult.timestamp;
                      }
                      return 'Unknown date';
                    }
                  })()}
                </p>
              )}
            </div>

            {/* Task History Content */}
            <ScrollArea className="flex-1 max-h-[calc(100vh-220px)]">
              <div ref={taskHistoryScrollRef} className="p-4 space-y-3">
                {selectedTaskResult ? (
                  /* Show selected task result */
                  renderTaskResultDisplay(selectedTaskResult)
                ) : taskHistoryLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600">Loading task history...</p>
                    </div>
                  </div>
                ) : taskHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-600">No tasks found for this document</p>
                  </div>
                ) : (
                  taskHistory.map((task, index) => {
                    const taskName = task.task_name || AVAILABLE_TASKS.find(t => t.id === task.task_type || t.task_type === task.task_type)?.name || task.task_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    // Handle timestamp - use time_received or timestamp field
                    let timestamp = 'Unknown date';
                    try {
                      const timeValue = (task as any).time_received || (task as any).timestamp;
                      if (timeValue) {
                        const date = new Date(timeValue);
                        if (!isNaN(date.getTime())) {
                          timestamp = date.toLocaleString();
                        }
                      }
                    } catch (e) {
                      console.error('Invalid timestamp:', (task as any).time_received, (task as any).timestamp);
                    }

                    return (
                      <div
                        key={task.analysis_id || index}
                        className="border border-gray-200 rounded-lg p-3 hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
                        onClick={() => {
                          // Extract the actual result data - use 'analysis' or 'result' field
                          let resultContent = (task as any).analysis || (task as any).result;

                          // If result has nested structure, extract it
                          if (resultContent && typeof resultContent === 'object') {
                            if (resultContent.result !== undefined) {
                              resultContent = resultContent.result;
                            } else if (resultContent.output !== undefined) {
                              resultContent = resultContent.output;
                            } else if (resultContent.analysis !== undefined) {
                              resultContent = resultContent.analysis;
                            }
                          }

                          // Convert to string if it's an object
                          const contentStr = typeof resultContent === 'string'
                            ? resultContent
                            : (resultContent ? JSON.stringify(resultContent) : '{}');

                          // Get timestamp
                          const timeValue = (task as any).time_received || (task as any).timestamp;
                          const timestampStr = timeValue || timestamp;

                          // Create a TaskResultAttachment from the history item
                          const taskResult: TaskResultAttachment = {
                            id: task.analysis_id,
                            taskName: taskName,
                            taskType: task.task_type,
                            timestamp: timestampStr,
                            content: contentStr,
                            documentId: task.doc_id || task.selected_number || task.application_number
                          };
                          setSelectedTaskResult(taskResult);
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm truncate">
                              {taskName}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                {timestamp}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Question History Panel */}
        {showQuestionHistory && ['patent', 'publication', 'application', 'upload'].includes(documentType.toLowerCase()) && (
          <div
            className="flex-shrink-0 min-w-0 relative border-l border-gray-200 flex flex-col"
            style={{
              width: `${questionWidth}px`,
              minWidth: '300px',
              maxWidth: '50vw'
            }}
          >
            {/* Resize handle */}
            <div
              className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10 hover:bg-blue-500 ${isResizingQuestion ? 'bg-blue-500' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsResizingQuestion(true);
              }}
              title="Drag to resize width"
            />

            <CustomQuestionSection
              showQuestionHistory={showQuestionHistory}
              setShowQuestionHistory={setShowQuestionHistory}
              questions={questions}
              questionText={questionText}
              setQuestionText={setQuestionText}
              questionLoading={questionLoading}
              handleQuestionSubmit={handleQuestionSubmit}
              scrollDivRef={questionScrollRef}
              showTopButton={showTopButton}
              showBottomButton={showBottomButton}
              scrollToTop={scrollToTop}
              scrollToBottom={scrollToBottom}
              toggleFullscreen={toggleQuestionHistoryFullscreen}
              isFullscreen={false}
              onDeleteQuestion={handleDeleteQuestion}
              customPrompts={filteredCustomPrompts}
              standardPrompts={standardDocumentPrompts}
              onCreatePrompt={handleCreatePrompt}
            />
          </div>
        )}

        {/* Fullscreen Question History Modal */}
        <Dialog open={showQuestionHistoryFullscreen} onOpenChange={setShowQuestionHistoryFullscreen}>
          <DialogContent className="!max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] p-0 flex flex-col sm:!max-w-[95vw] md:!max-w-[95vw] lg:!max-w-[95vw] [&>button]:hidden">
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-xl font-semibold text-gray-900">Question History - {documentName}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowQuestionHistoryFullscreen(false)}
                  className="h-8 w-8 p-0"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Messages Container */}
              <div ref={questionScrollRef} onScroll={handleScroll} className="relative flex-1 overflow-y-auto py-6 space-y-4 bg-white">
                {questions && questions.length > 0 ? (
                  <>
                    {questions.map((item, idx) => {
                      const isValidTimestamp = !isNaN(new Date(item.timestamp).getTime());

                      // Handle response in different formats
                      let responseText = '';
                      if (typeof item.response === 'string') {
                        responseText = item.response;
                      } else if (typeof item.response === 'object' && item.response.output) {
                        responseText = item.response.output;
                      } else {
                        responseText = JSON.stringify(item.response);
                      }

                      return (
                        <div key={idx} className="w-full px-4">
                          {/* Divider */}
                          {idx > 0 && (
                            <div className="flex justify-center my-6">
                              <div className="w-full border-t border-gray-300"></div>
                            </div>
                          )}

                          {/* Timestamp */}
                          {isValidTimestamp && (
                            <div className="text-center text-sm text-gray-500 mb-3">
                              {new Date(item.timestamp).toLocaleString()}
                            </div>
                          )}

                          {/* Question (Right-aligned, blue) */}
                          <div className="flex justify-end mb-3 group">
                            <div className="flex items-start gap-2">
                              <div className="flex items-start gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-gray-100"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(item.input_text);
                                    } catch (error) {
                                      console.error('Failed to copy question:', error);
                                    }
                                  }}
                                  title="Copy question"
                                >
                                  <Copy className="w-4 h-4 text-gray-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-red-100"
                                  onClick={() => handleDeleteQuestion('', idx)}
                                  title="Delete question"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                              <div className="max-w-[75%] p-4 rounded-lg bg-blue-500 text-white shadow-md">
                                <div className="whitespace-pre-wrap break-words" style={getFontSizeStyle()}>
                                  {item.input_text.replace(/\n{2,}/g, '\n')}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Response (Left-aligned, gray) */}
                          <div className="flex justify-start mb-3 group">
                            <div className="flex items-start gap-2">
                              <div className="max-w-[75%] p-4 rounded-lg bg-gray-100 border border-gray-200 text-gray-900 shadow-md">
                                {responseText === '...' ? (
                                  <div className="flex items-center gap-2 text-gray-500">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm">Generating response...</span>
                                  </div>
                                ) : (
                                  <div className="prose max-w-none text-gray-900" style={getFontSizeStyle()}>
                                    <Markdown>{responseText}</Markdown>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-start gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-gray-100"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(responseText);
                                    } catch (error) {
                                      console.error('Failed to copy response:', error);
                                    }
                                  }}
                                  title="Copy response"
                                >
                                  <Copy className="w-4 h-4 text-gray-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-red-100"
                                  onClick={() => handleDeleteQuestion('', idx)}
                                  title="Delete conversation"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 text-lg">
                    No question history yet. Ask a question about this document!
                  </div>
                )}

                {/* Scroll buttons */}
                {showTopButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-6 right-6 rounded-full shadow-lg"
                    onClick={scrollToTop}
                    title="Scroll to top"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                )}
                {showBottomButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute bottom-6 right-6 rounded-full shadow-lg"
                    onClick={scrollToBottom}
                    title="Scroll to bottom"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Input Area */}
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="w-full px-8">
                  <div className="flex gap-4">
                    <textarea
                      className="flex-grow resize-none min-h-[80px] max-h-[200px] px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={questionText}
                      onChange={(e) => setQuestionText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (questionText.trim() && !questionLoading) {
                            handleQuestionSubmit();
                          }
                        }
                      }}
                      placeholder="Ask a question about this document..."
                      disabled={questionLoading}
                    />
                    <div className="flex flex-col gap-2">
                      {/* Quick Prompts Dropdown */}
                      {((filteredCustomPrompts && filteredCustomPrompts.length > 0) || (standardDocumentPrompts && standardDocumentPrompts.length > 0)) && (
                        <DropdownMenu open={showQuickPromptsMenu} onOpenChange={setShowQuickPromptsMenu}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="px-3"
                              disabled={questionLoading}
                            >
                              <Sparkles className="w-4 h-4 mr-2" />
                              Quick Prompts
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-64 max-h-96 overflow-y-auto">
                            {/* Create New Prompt Button */}
                            <DropdownMenuItem
                              onClick={handleCreatePrompt}
                              className="cursor-pointer text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Sparkles className="w-4 h-4 mr-2" />
                              Create New Prompt
                            </DropdownMenuItem>
                            <div className="border-t border-gray-200 my-1" />

                            {/* Custom Prompts Section */}
                            {filteredCustomPrompts && filteredCustomPrompts.length > 0 && (
                              <>
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Your Custom Prompts
                                </div>
                                {filteredCustomPrompts.map((prompt) => (
                                  <DropdownMenuItem
                                    key={prompt.prompt_id}
                                    onClick={() => {
                                      setQuestionText(prompt.prompt);
                                      setShowQuickPromptsMenu(false);
                                    }}
                                    className="cursor-pointer py-2"
                                  >
                                    <div className="flex flex-col gap-1">
                                      <span className="font-medium text-sm">{prompt.label}</span>
                                      <span className="text-xs text-gray-500 line-clamp-2">{prompt.prompt}</span>
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                                <div className="border-t border-gray-200 my-1" />
                              </>
                            )}

                            {/* Standard Document Prompts Section */}
                            {standardDocumentPrompts && standardDocumentPrompts.length > 0 && (
                              <>
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Standard Document Prompts
                                </div>
                                {standardDocumentPrompts.map((prompt) => (
                                  <DropdownMenuItem
                                    key={prompt.prompt_id}
                                    onClick={() => {
                                      setQuestionText(prompt.prompt);
                                      setShowQuickPromptsMenu(false);
                                    }}
                                    className="cursor-pointer py-2"
                                  >
                                    <div className="flex flex-col gap-1">
                                      <span className="font-medium text-sm">{prompt.label}</span>
                                      <span className="text-xs text-gray-500 line-clamp-2">{prompt.prompt}</span>
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <Button
                        onClick={handleQuestionSubmit}
                        disabled={questionLoading || !questionText.trim()}
                        className="px-8 h-auto"
                      >
                        {questionLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          'Submit'
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    Press Enter to submit, Shift+Enter for new line
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Task Result Fullscreen Modal */}
        {showTaskResultFullscreen && selectedTaskResult && (
          <Dialog open={showTaskResultFullscreen} onOpenChange={setShowTaskResultFullscreen}>
            <DialogContent
              className="flex flex-col p-0"
              style={{ width: '90vw', maxWidth: '1200px', height: '95vh', maxHeight: '95vh' }}
            >
              <div className="flex-1 overflow-hidden">
                <ScrollArea className="h-full">
                  <div className="p-6 pt-12">
                    {renderTaskResultDisplay(selectedTaskResult, false)}
                  </div>
                </ScrollArea>
              </div>
            </DialogContent>
          </Dialog>
        )}
        </div>
      )}

      {/* Comment Popup - appears when text is selected */}
      {showCommentPopup && (
        <div
          className="fixed z-[100] bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-80"
          style={{
            left: Math.min(Math.max(10, commentPopupPosition.x - 160), window.innerWidth - 340),
            // If near bottom, position popup ABOVE the selection point
            top: commentPopupPosition.y > window.innerHeight - 250
              ? Math.max(10, commentPopupPosition.y - 220)  // Position above
              : Math.min(commentPopupPosition.y + 10, window.innerHeight - 250),  // Position below
          }}
        >
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs text-gray-500">Selected text:</div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => {
                  navigator.clipboard.writeText(selectedText);
                  addNotification({ type: 'success', title: 'Copied', message: 'Text copied to clipboard', duration: 2000 });
                }}
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>
            </div>
            <div className="text-sm bg-yellow-50 p-2 rounded border border-yellow-200 max-h-20 overflow-y-auto">
              "{selectedText.length > 100 ? selectedText.substring(0, 100) + '...' : selectedText}"
            </div>
          </div>
          <textarea
            className="w-full p-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Add your comment..."
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowCommentPopup(false);
                setSelectedText('');
                setNewCommentText('');
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={addComment}
              disabled={!newCommentText.trim()}
            >
              <MessageSquarePlus className="w-3 h-3 mr-1" />
              Add Comment
            </Button>
          </div>
        </div>
      )}

      {/* Comments Panel - slides in from the right */}
      {showCommentsPanel && (
        <div className="fixed inset-y-0 right-0 w-96 bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col">
          {/* Panel Header */}
          <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquarePlus className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Comments</h3>
                <Badge variant="secondary">{comments.length}</Badge>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowCommentsPanel(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Select text in the document to add a comment
            </p>
          </div>

          {/* Comments List - with proper scroll container */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-4 space-y-3">
              {comments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No comments yet</p>
                  <p className="text-xs mt-1">Select text to add a comment</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                  >
                    {/* Selected text quote */}
                    <div className="text-xs bg-yellow-50 p-2 rounded border-l-2 border-yellow-400 mb-2 text-gray-700 italic">
                      "{comment.selected_text.length > 80 ? comment.selected_text.substring(0, 80) + '...' : comment.selected_text}"
                    </div>

                    {/* Comment text or edit form */}
                    {editingCommentId === comment.id ? (
                      <div className="space-y-2">
                        <textarea
                          className="w-full p-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={3}
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          autoFocus
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditingCommentText('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => updateComment(comment.id)}
                            disabled={!editingCommentText.trim()}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-800">{comment.comment_text}</div>
                    )}

                    {/* Meta and actions */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                      <div className="text-xs text-gray-400">
                        {new Date(comment.created_at).toLocaleDateString()}
                        {comment.section && <span className="ml-2">• {comment.section}</span>}
                      </div>
                      {editingCommentId !== comment.id && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              setEditingCommentId(comment.id);
                              setEditingCommentText(comment.comment_text);
                            }}
                            title="Edit comment"
                          >
                            <Edit3 className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500 hover:text-red-700"
                            onClick={() => deleteComment(comment.id)}
                            title="Delete comment"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Modal */}
      {showFullscreen && (
        <Dialog open={showFullscreen} onOpenChange={setShowFullscreen}>
          <DialogContent className="!max-w-[98vw] w-[98vw] h-[98vh] max-h-[98vh] p-0 flex flex-col sm:!max-w-[98vw] md:!max-w-[98vw] lg:!max-w-[98vw]">
            <div className="flex-1 overflow-hidden">
              <DocumentViewer
                document={document}
                onClose={() => setShowFullscreen(false)}
                isFullWidth={true}
                onViewApplication={onViewApplication}
                initialTab={activeTab}
                hideCloseButton={true}
                hideFullscreenButton={true}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Custom Prompt Modal */}
      <CustomPromptModal
        open={showCustomPromptModal}
        onOpenChange={async (open) => {
          setShowCustomPromptModal(open);
          if (!open) {
            setEditingPrompt(null);
            // Refresh prompts when modal closes
            try {
              const response = await userApi.fetchPrompts();
              if (response.prompts) {
                setCustomPrompts(response.prompts);
              }
            } catch (error) {
              console.error('Failed to refresh prompts on modal close:', error);
            }
          }
        }}
        onSave={handleSaveCustomPrompt}
        editingPrompt={editingPrompt}
      />
    </div>
  );
}
